// RiskAssessmentTable Component - TypeScript Fixed with Grouped Display
// frontend/src/components/RiskMapping/RiskAssessmentTable.tsx

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Download, RefreshCw, Filter, Search, Eye, EyeOff, Edit3,
  AlertTriangle, FileText, CheckCircle, XCircle,
  ChevronDown, ChevronUp, Plus, Brain, Target, Shield, BarChart3, Settings, X, Clock, Info, Calculator,
  HelpCircle, ExternalLink
} from 'lucide-react';
import api from '../../api';
import { backgroundJobsAPI, BackgroundJob } from '../../services/backgroundJobsAPI';
import AuditTrailModal from './AuditTrailModal';
import { RiskScoringModal } from './RiskScoringModal';
import { RiskScopingWizard, ProcessScopingQuestionnaire } from '../RiskScoping';
import { useTenant } from '../../contexts/TenantContext';
import { RiskSummaryPanel } from './RiskSummaryPanel';

// Helper function to format JSON rationale to readable text
const formatRationaleText = (rationale: string | undefined): string => {
  if (!rationale) return 'No rationale provided';

  // Check if it looks like JSON
  const trimmed = rationale.trim();
  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);

      // Format the JSON into readable text
      const parts: string[] = [];

      if (parsed.processCharacteristics) {
        const pc = parsed.processCharacteristics;
        if (pc.complexity) {
          parts.push(`Process Complexity: ${pc.complexity}`);
        }
        if (pc.factors && Array.isArray(pc.factors)) {
          parts.push(`Factors: ${pc.factors.join(', ')}`);
        }
      }

      if (parsed.assessmentBasis) {
        parts.push(`Assessment Basis: ${parsed.assessmentBasis}`);
      }

      if (parsed.confidence) {
        parts.push(`Confidence: ${parsed.confidence}`);
      }

      // If we successfully parsed and formatted, return the result
      if (parts.length > 0) {
        return parts.join('\n');
      }

      // Fallback: if it's JSON but doesn't match expected structure,
      // try to extract any string values
      const extractStrings = (obj: unknown): string[] => {
        const strings: string[] = [];
        if (typeof obj === 'string') {
          strings.push(obj);
        } else if (Array.isArray(obj)) {
          obj.forEach(item => strings.push(...extractStrings(item)));
        } else if (obj && typeof obj === 'object') {
          Object.values(obj).forEach(val => strings.push(...extractStrings(val)));
        }
        return strings;
      };

      const extracted = extractStrings(parsed);
      if (extracted.length > 0) {
        return extracted.join('\n');
      }
    } catch {
      // Not valid JSON, return as-is
    }
  }

  return rationale;
};

// TypeScript Interfaces
interface RiskAssessmentRow {
  id: number;
  processId: string;
  processStepId: string;
  stepName: string;
  riskId: string;
  l4Risk: string;
  l4Description: string;
  rationale: string;
  rationaleValidationStatus?: 'approved' | 'rejected' | 'pending';
  rationaleValidatedBy?: string;
  rationaleValidatedAt?: string;
  appliedRiskDescription: string;
  // New structured fields
  riskCause?: string;
  riskEvent?: string;
  riskImpact?: string;
  likelihoodRationale?: string;
  inherentRiskLevel: string;
  likelihood: number;
  impact: number;
  likelihoodScoreCalculated?: number;
  impactScoreCalculated?: number;
  finalRiskScoreCalculated?: number;
  riskPriorityScore: number;
  // Second pass fields
  regulatoryTaxonomy: string;
  businessTranslation: string;
  auditorMightAsk: string;
  keyEvidenceRequired: string;
  typicalControlCategories: string;
  businessImpactTranslation: string;
  // Metadata
  lastAssessed: string;
  assessedBy: string;
  reviewStatus: 'approved' | 'pending';
  reviewDate?: string;
  aiGenerated: boolean;
  // Existing controls with classification
  existing_controls?: Array<{
    controlId?: string;
    description: string;
    type: string;
    classificationReasoning?: string;
    frequency?: string;
    owner?: string;
    effectiveness?: number;
    source?: string;
  }>;
  // Framework violations from AI assessment
  frameworkViolations?: Array<{
    frameworkName: string;
    standardId: string;
    standardName: string;
    violationDescription: string;
    requirement?: string;
    pageNumber?: string;
    sectionReference?: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
  }>;
}

interface ProcessStep {
  id: number | string;
  name: string;
  description?: string;
  category?: string;
  assignee?: string;
  risk_level?: string;
  controls?: unknown[];
  risks?: unknown[];
  stepNumber?: number;
  order?: number;
  // Database fields
  process_id?: string;
  process_step_id?: string | number;
  database_step_id?: string;
  frequency?: string;
  volume?: string;
  performing_country?: string;
  legal_entity?: string;
  supporting_model?: string;
  third_party_business_type?: string;
  // Additional fields for process map integration
  map_title?: string;
  map_level?: string;
  map_id?: string | number;
  // Legacy field names for compatibility
  processId?: string;
  stepId?: string | number;
  stepName?: string;
}

interface RiskControlMapping {
  riskId?: string | null;
  riskName?: string | null;
  riskDescription?: string | null;
  riskTaxonomyPath?: string | null;
  inherentLikelihood?: number | null;
  inherentImpact?: number | null;
  inherentRiskScore?: number | null;
  inherentRiskLevel?: string | null;
  timeframe?: string | null;
}

interface ProcessStepMapping {
  processStep: {
    id: number;
    processStepId: string;
    stepNumber: number;
    processStepName: string;
    description: string;
  };
  riskControlMappings: RiskControlMapping[];
}

interface RiskAssessmentTableProps {
  projectId?: number;
  processSteps: ProcessStep[];
  mappingData: ProcessStepMapping[];
  onExport: (format?: "pdf" | "excel" | "json") => Promise<void>;
  onRefresh?: () => void;
  error?: string | null;
  onRiskSelect?: (riskId: number) => void; // Optional callback when a risk is selected
}

interface Filters {
  searchTerm: string;
  riskLevel: string;
  taxonomy: string;
  processStep: string;
  priorityScore: string;
}

interface SortConfig {
  key: keyof RiskAssessmentRow | null;
  direction: 'asc' | 'desc';
}

interface GeneratedRisk {
  riskId: string;
  riskName: string;
  riskDescription: string;
  riskTaxonomyPath: string;
  inherentLikelihood: number;
  inherentImpact: number;
  inherentRiskScore: number;
  inherentRiskLevel: string;
  timeframe: string;
}

// Component to display Applied Risk Description in Cause, Event, Impact format
interface AppliedRiskDescriptionDisplayProps {
  riskCause?: string;
  riskEvent?: string;
  riskImpact?: string;
  appliedRiskDescription?: string; // Fallback for backwards compatibility
}

const AppliedRiskDescriptionDisplay: React.FC<AppliedRiskDescriptionDisplayProps> = ({
  riskCause,
  riskEvent,
  riskImpact,
  appliedRiskDescription
}) => {
  // Use structured data if available, otherwise parse the description as fallback
  let cause = riskCause;
  let event = riskEvent;
  let impact = riskImpact;

  // If structured data is not available, fall back to parsing the description
  if (!cause || !event || !impact) {
    const parsed = parseRiskDescription(appliedRiskDescription || '');
    cause = cause || parsed.cause;
    event = event || parsed.event;
    impact = impact || parsed.impact;
  }

  // Helper function to parse the applied risk description (fallback)
  function parseRiskDescription(description: string) {
    if (!description) {
      return {
        cause: 'Not specified',
        event: 'Not specified',
        impact: 'Not specified'
      };
    }

    // Try to extract structured content if it exists
    const causeMatch = description.match(/\(CAUSE\)\s*(.+?)(?=\(EVENT\)|$)/i);
    const eventMatch = description.match(/\(EVENT\)\s*(.+?)(?=\(IMPACT\)|$)/i);
    const impactMatch = description.match(/\(IMPACT\)\s*(.+?)$/i);

    if (causeMatch && eventMatch && impactMatch) {
      // Structured format already exists
      return {
        cause: causeMatch[1].trim(),
        event: eventMatch[1].trim(),
        impact: impactMatch[1].trim()
      };
    }

    // If not structured, try to intelligently break down the description
    const sentences = description.split(/[.;]\s*/).filter(s => s.trim().length > 0);

    if (sentences.length >= 3) {
      return {
        cause: sentences[0].trim(),
        event: sentences[1].trim(),
        impact: sentences.slice(2).join('. ').trim()
      };
    } else if (sentences.length === 2) {
      return {
        cause: sentences[0].trim(),
        event: 'Risk materialization occurs',
        impact: sentences[1].trim()
      };
    } else {
      // Single sentence - try to infer structure
      const singleSentence = description.trim();

      // Look for conditional patterns
      if (singleSentence.toLowerCase().includes('when') || singleSentence.toLowerCase().includes('if')) {
        const parts = singleSentence.split(/\b(?:when|if)\b/i);
        if (parts.length >= 2) {
          return {
            cause: `When ${parts[1].trim()}`,
            event: 'The identified risk scenario occurs',
            impact: parts[0].trim() || 'Business operations may be affected'
          };
        }
      }

      // Fallback to generic structure
      return {
        cause: 'Underlying conditions create vulnerability',
        event: singleSentence,
        impact: 'Business operations may be disrupted'
      };
    }
  }

  return (
    <div className="space-y-3">
      {/* Cause */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-semibold">
            Cause
          </div>
          <span className="text-xs text-blue-600 font-medium">What would cause the risk?</span>
        </div>
        <p className="text-sm text-blue-800 leading-relaxed">
          {cause}
        </p>
      </div>

      {/* Event */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-yellow-500 text-white px-2 py-1 rounded text-xs font-semibold">
            Event
          </div>
          <span className="text-xs text-yellow-600 font-medium">What's the risk?</span>
        </div>
        <p className="text-sm text-yellow-800 leading-relaxed">
          {event}
        </p>
      </div>

      {/* Impact */}
      <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-r">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
            Impact
          </div>
          <span className="text-xs text-red-600 font-medium">What's the impact?</span>
        </div>
        <p className="text-sm text-red-800 leading-relaxed">
          {impact}
        </p>
      </div>
    </div>
  );
};

// Component to display Enhanced Likelihood with rating and rationale
interface EnhancedLikelihoodDisplayProps {
  likelihood: number;
  likelihoodRationale?: string;
  onEditScoring?: () => void;  // New prop for opening scoring modal
  isManuallyAssessed?: boolean; // Track if user has manually scored
}

const EnhancedLikelihoodDisplay: React.FC<EnhancedLikelihoodDisplayProps> = ({
  likelihood,
  likelihoodRationale,
  onEditScoring,
  isManuallyAssessed = false
}) => {
  // Helper function to get likelihood label and description
  const getLikelihoodInfo = (rating: number) => {
    switch (rating) {
      case 1:
        return { label: 'Remote', timeframe: '5+ years', color: 'green' };
      case 2:
        return { label: 'Unlikely', timeframe: '2-5 years', color: 'blue' };
      case 3:
        return { label: 'Possible', timeframe: '1-2 years', color: 'yellow' };
      case 4:
        return { label: 'Likely', timeframe: '6-12 months', color: 'orange' };
      case 5:
        return { label: 'Almost Certain', timeframe: '< 6 months', color: 'red' };
      default:
        return { label: 'Unknown', timeframe: 'N/A', color: 'gray' };
    }
  };

  const { label, timeframe, color } = getLikelihoodInfo(likelihood);

  const getColorClasses = (colorName: string) => {
    switch (colorName) {
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          badge: 'bg-green-100 text-green-800'
        };
      case 'blue':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          badge: 'bg-blue-100 text-blue-800'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          badge: 'bg-yellow-100 text-yellow-800'
        };
      case 'orange':
        return {
          bg: 'bg-orange-50',
          border: 'border-orange-200',
          text: 'text-orange-800',
          badge: 'bg-orange-100 text-orange-800'
        };
      case 'red':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-800',
          badge: 'bg-red-100 text-red-800'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          badge: 'bg-gray-100 text-gray-800'
        };
    }
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className={`${colorClasses.bg} ${colorClasses.border} border rounded-lg p-4 space-y-3`}>
      {/* Likelihood Header */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          {/* Assessment Type Tag - Now on top */}
          <div className="flex items-center gap-2">
            {isManuallyAssessed ? (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium border border-blue-200">
                ✓ Manually Assessed
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium border border-purple-200">
                🤖 AI Generated
              </span>
            )}
          </div>
          {/* Score and Timeframe */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`${colorClasses.badge} px-3 py-1 rounded-full text-sm font-semibold`}>
              {likelihood}/5 - {label}
            </div>
            <div className="text-xs text-gray-600">
              Timeframe: {timeframe}
            </div>
          </div>
        </div>
        {onEditScoring && (
          <button
            onClick={onEditScoring}
            className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit Scoring
          </button>
        )}
      </div>

      {/* Likelihood Statement */}
      {likelihoodRationale && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Likelihood Statement
          </div>
          <p className={`text-sm ${colorClasses.text} leading-relaxed whitespace-pre-wrap`}>
            {formatRationaleText(likelihoodRationale)}
          </p>
        </div>
      )}

      {/* Fallback if no rationale */}
      {!likelihoodRationale && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
            Likelihood Statement
          </div>
          <p className={`text-sm ${colorClasses.text} leading-relaxed italic`}>
            This risk has been assessed as {label.toLowerCase()} based on process characteristics and operational factors.
          </p>
        </div>
      )}
    </div>
  );
};

// Component for compact likelihood badge in main table
interface LikelihoodBadgeProps {
  likelihood: number;
}

const LikelihoodBadge: React.FC<LikelihoodBadgeProps> = ({ likelihood }) => {
  const getLikelihoodInfo = (rating: number) => {
    switch (rating) {
      case 1:
        return { label: 'Medium', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
      case 2:
        return { label: 'Medium', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
      case 3:
        return { label: 'Medium', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
      case 4:
        return { label: 'High', color: 'bg-orange-50 text-orange-700 border-orange-200' };
      case 5:
        return { label: 'High', color: 'bg-red-50 text-red-700 border-red-200' };
      default:
        return { label: 'Unknown', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const { label, color } = getLikelihoodInfo(likelihood);

  return (
    <div className={`flex flex-col items-center px-2 py-1 rounded border ${color} shadow-sm`}>
      <span className="text-lg font-bold">{likelihood}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
};

// Component for impact badge in main table
interface ImpactBadgeProps {
  impact: number;
}

const ImpactBadge: React.FC<ImpactBadgeProps> = ({ impact }) => {
  const getImpactInfo = (rating: number) => {
    switch (rating) {
      case 1:
        return { label: 'Minimal', color: 'bg-green-50 text-green-700 border-green-200' };
      case 2:
        return { label: 'Minor', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 3:
        return { label: 'Moderate', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
      case 4:
        return { label: 'Likely', color: 'bg-orange-50 text-orange-700 border-orange-200' };
      case 5:
        return { label: 'Severe', color: 'bg-red-50 text-red-700 border-red-200' };
      default:
        return { label: 'Unknown', color: 'bg-gray-50 text-gray-700 border-gray-200' };
    }
  };

  const { label, color } = getImpactInfo(impact);

  return (
    <div className={`flex flex-col items-center px-2 py-1 rounded border ${color} shadow-sm`}>
      <span className="text-lg font-bold">{impact}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
};

const RiskAssessmentTable: React.FC<RiskAssessmentTableProps> = ({
  projectId,
  processSteps = [],
  mappingData = [],
  onExport,
  onRefresh,
  error = null,
  onRiskSelect
}) => {
  // Get tenant from context
  const { tenantData } = useTenant();
  const [effectiveTenantId, setEffectiveTenantId] = useState<number>(1);

  // Fetch current user's tenant ID on mount
  useEffect(() => {
    const fetchUserTenant = async () => {
      try {
        const response = await api.get('/api/auth/me');
        if (response.data.tenant_id) {
          setEffectiveTenantId(response.data.tenant_id);
          console.log('✅ User tenant_id:', response.data.tenant_id);
        } else if (tenantData?.id) {
          setEffectiveTenantId(tenantData.id);
        }
      } catch (err) {
        console.error('Failed to fetch user tenant:', err);
        if (tenantData?.id) {
          setEffectiveTenantId(tenantData.id);
        }
      }
    };
    fetchUserTenant();
  }, [tenantData]);

  // State Management
  const [loading, setLoading] = useState<boolean>(false);
  const [riskAssessmentData, setRiskAssessmentData] = useState<RiskAssessmentRow[]>([]);
  const [filteredData, setFilteredData] = useState<RiskAssessmentRow[]>([]);
  const [riskSummary, setRiskSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedProcessSteps, setExpandedProcessSteps] = useState<Set<string>>(new Set());
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'processStepId', direction: 'asc' });
  const [processMapSteps, setProcessMapSteps] = useState<ProcessStep[]>([]);
  const [generatingAssessment, setGeneratingAssessment] = useState<boolean>(false);
  const [partialDataCleared, setPartialDataCleared] = useState<boolean>(false);
  const [generationProgress, setGenerationProgress] = useState<{
    current: number;
    total: number;
    currentStep: string;
    message: string;
    isError?: boolean;
  } | null>(null);

  // Background job state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [backgroundJob, setBackgroundJob] = useState<BackgroundJob | null>(null);
  const [jobPollingInterval, setJobPollingInterval] = useState<NodeJS.Timeout | null>(null);

  // Filter states
  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    riskLevel: 'all',
    taxonomy: 'all',
    processStep: 'all',
    priorityScore: 'all'
  });

  // Edit mode states
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<RiskAssessmentRow>>({});

  // Audit trail modal states
  const [auditTrailModalOpen, setAuditTrailModalOpen] = useState(false);
  const [selectedRiskForAudit, setSelectedRiskForAudit] = useState<{ id: number; riskId: string } | null>(null);

  // Risk scoring modal states
  const [scoringModalOpen, setScoringModalOpen] = useState(false);
  const [selectedRiskForScoring, setSelectedRiskForScoring] = useState<{
    riskId: string;
    processStepName: string;
    likelihood: number;
    impact: number;
    controlEffectiveness?: number;
  } | null>(null);

  // Risk scoping modal state
  const [showRiskScopingModal, setShowRiskScopingModal] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [scopingResult, setScopingResult] = useState<any>(null);

  // Cancel job confirmation dialog state
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    type: 'cancel' | 'warning' | 'success';
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    type: 'cancel',
    onConfirm: () => {}
  });

  // Regulatory frameworks mapping
  const regulatoryFrameworks: Record<string, string> = {
    'Basel III Credit Risk Framework': 'Banking regulations requiring systematic assessment of credit exposures and capital adequacy',
    'SOX Section 404': 'Internal controls over financial reporting must be assessed and certified annually',
    'Basel II Pillar 2': 'Supervisory review process requiring banks to assess capital adequacy relative to risk profile',
    'COSO ERM Framework': 'Enterprise risk management framework for identifying and managing organizational risks',
    'GDPR Article 32': 'Data protection regulations requiring appropriate technical and organizational security measures',
    'OCC Comptroller Handbook': 'Banking supervision guidance for risk assessment and management practices',
    'FFIEC IT Examination Handbook': 'Guidance for examining information technology in financial institutions'
  };

  // Use process steps from props instead of making API calls
  const initializeProcessSteps = useCallback(() => {
    console.log('🔍 Initializing process steps from props:', processSteps);
    console.log('🔍 Process steps type:', typeof processSteps, 'Length:', processSteps?.length);
    
    if (processSteps && processSteps.length > 0) {
      console.log('📊 Using process steps from props:', processSteps.length);
      
      // Format steps consistently for risk assessment - preserve actual IDs
      console.log('🔍 Raw step data sample:', processSteps[0]);
      console.log('🔍 Step fields:', processSteps[0] ? Object.keys(processSteps[0]) : 'no steps');
      console.log('🔍 Looking for process_id in step:', processSteps[0]?.process_id);

      // Format ALL steps - preserve original numbering (including Start/End in the sequence)
      // Filtering will happen at display/query level, not here
      const formattedSteps = processSteps.map((step: ProcessStep, index: number) => ({
        id: step.id || `step-${index}`,
        name: step.name || `Step ${index + 1}`,
        description: step.description || `Process step: ${step.name}`,
        order: step.order || index + 1,
        risk_level: step.risk_level || 'Medium',
        controls: step.controls || [],
        category: step.category || 'Process',
        stepNumber: step.stepNumber || index + 1,
        map_title: 'Process Flow',
        map_level: 'actual',
        // Preserve actual process IDs from the data and format step ID properly
        process_id: step.process_id || step.processId,
        process_step_id: step.process_step_id || step.stepId || step.id,
        processId: step.process_id || step.processId,
        stepId: step.process_step_id || step.stepId || step.id
      }));

      console.log(`✅ Formatted ${processSteps.length} process steps (including Start/End - filtering happens at display level)`);
      setProcessMapSteps(formattedSteps);
    } else {
      console.log('❌ No process steps provided in props, using empty state');
      setProcessMapSteps([]);
    }
  }, [processSteps]);

  // REMOVED: This function was creating hardcoded generic risks and is no longer needed

  // REMOVED: This function was generating too many risks and is no longer used

  // Fetch risk summary
  const fetchRiskSummary = useCallback(async () => {
    if (!projectId) return;

    try {
      setSummaryLoading(true);
      const response = await api.get(`/api/risk-mapping/${projectId}/summary`);

      if (response.data.success) {
        setRiskSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch risk summary:', error);
      setRiskSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [projectId]);

  // Fetch summary when risk data changes
  useEffect(() => {
    if (riskAssessmentData.length > 0) {
      fetchRiskSummary();
    }
  }, [riskAssessmentData.length, fetchRiskSummary]);

  // Helper function to determine risk level from score
  const determineRiskLevel = (score: number): string => {
    if (score >= 20) return 'CRITICAL';
    if (score >= 15) return 'HIGH';
    if (score >= 8) return 'MEDIUM';
    return 'LOW';
  };

  // Convert comprehensive risk assessment result to table format
  const convertComprehensiveRiskToTableFormat = (
    risk: any,
    riskAssessment: any,
    stepIndex: number,
    processStep: any,
    riskIndex: number
  ): RiskAssessmentRow | null => {
    console.log('🔍 Processing comprehensive risk:', risk.riskCategory, 'for step:', processStep.name);

    if (!risk) {
      console.log('⚠️ No risk data provided');
      return null;
    }

    // Create unique assessment ID
    const assessmentId = (stepIndex * 10) + riskIndex + 1;

    // Only create assessment if we have valid risk data
    if (!risk.riskCategory || !risk.riskDescription || !risk.riskId) {
      console.log('⚠️ Incomplete risk data, skipping:', risk);
      return null;
    }

    // Generate proper step ID format from processStep if available
    const processId = processStep?.process_id || processStep?.processId || `PROC-${String(stepIndex + 1).padStart(3, '0')}`;
    const stepId = processStep?.process_step_id && processStep.process_step_id.includes('_STEP_')
      ? processStep.process_step_id
      : `${processId}_STEP_${String(stepIndex + 1).padStart(2, '0')}`;

    const assessment: RiskAssessmentRow = {
      id: assessmentId,
      processId: processId,
      processStepId: stepId,
      stepName: processStep.name || riskAssessment.processStep || `Step ${stepIndex + 1}`,
      riskId: risk.riskId,
      l4Risk: risk.riskCategory,
      l4Description: risk.riskDescription,
      // Use riskStatement if available, otherwise try to build from components, fallback to riskDescription
      rationale: risk.riskStatement || (() => {
        if (risk.riskCause && risk.riskEvent && risk.riskImpact) {
          return `Due to ${risk.riskCause}, there is a risk that ${risk.riskEvent}, which could result in ${risk.riskImpact}`;
        }
        return risk.riskIndicators?.join(', ') || risk.riskDescription;
      })(),
      appliedRiskDescription: risk.riskDescription,
      inherentRiskLevel: determineRiskLevel(risk.residualRisk || risk.inherentRisk || (risk.inherentProbability * risk.inherentImpact)),
      likelihood: (risk as any).likelihoodScoreCalculated || risk.inherentProbability,
      impact: (risk as any).impactScoreCalculated || risk.inherentImpact,
      likelihoodScoreCalculated: (risk as any).likelihoodScoreCalculated,
      impactScoreCalculated: (risk as any).impactScoreCalculated,
      finalRiskScoreCalculated: (risk as any).finalRiskScoreCalculated,
      riskPriorityScore: (risk as any).finalRiskScoreCalculated || risk.residualRisk || risk.inherentRisk || (risk.inherentProbability * risk.inherentImpact),
      regulatoryTaxonomy: risk.frameworks?.join(', ') || '',
      businessTranslation: risk.businessTranslation || '',
      auditorMightAsk: risk.auditorMightAsk || '',
      keyEvidenceRequired: risk.keyEvidenceRequired || '',
      typicalControlCategories: risk.existingControls?.map((control: any) => control.type).join(', ') || '',
      businessImpactTranslation: risk.businessImpactTranslation || '',
      lastAssessed: risk.assessmentDate || new Date().toISOString().split('T')[0],
      assessedBy: risk.assessedBy || 'AI Risk Assessment',
      reviewStatus: 'pending',
      aiGenerated: risk.aiGenerated !== false,
      existing_controls: risk.existingControls || [] // Add full controls array
    };

    return assessment;
  };

  // Load existing risk assessments from database
  const loadExistingAssessments = useCallback(async () => {
    console.log('🔍 Debug - projectId value:', projectId, 'Type:', typeof projectId);

    if (!projectId) {
      console.log('❌ No project ID available');
      return [];
    }

    try {
      console.log('🔍 Loading existing risk assessments from database for project:', projectId);
      const response = await api.get(`/api/comprehensive-risk-assessment/project/${projectId}/detailed`);

      if (response.data?.success && response.data?.processes) {
        const existingData: RiskAssessmentRow[] = [];

        // Process the detailed data from the new endpoint
        response.data.processes.forEach((processData: any, processIndex: number) => {
          const processStep = processData.processStep;
          console.log(`📊 Processing step ${processIndex + 1}: "${processStep}" with ${processData.risks?.length || 0} risks`);

          // Generate proper step ID format: PROC_ID_STEP_01
          const processId = processData.processId || `PROC-${String(processIndex + 1).padStart(3, '0')}`;
          const stepId = processData.stepId && processData.stepId.includes('_STEP_')
            ? processData.stepId
            : `${processId}_STEP_${String(processIndex + 1).padStart(2, '0')}`;

          processData.risks.forEach((risk: any, riskIndex: number) => {
            const assessment: RiskAssessmentRow = {
              id: risk.id || risk.database_id || (processIndex * 1000 + riskIndex), // Use database ID from API
              processId: processId,
              processStepId: stepId,
              stepName: processStep,
              riskId: risk.riskId,
              l4Risk: risk.risk_category || risk.riskCategory,
              l4Description: risk.riskDescription || '',
              // Use AI-generated risk statement directly from backend
              rationale: risk.riskStatement || '',
              rationaleValidationStatus: (risk as any).rationaleValidationStatus || 'pending',
              rationaleValidatedBy: (risk as any).rationaleValidatedBy,
              rationaleValidatedAt: (risk as any).rationaleValidatedAt,
              appliedRiskDescription: risk.riskDescription || '',
              // New structured fields from API
              riskCause: risk.riskCause || '',
              riskEvent: risk.riskEvent || '',
              riskImpact: risk.riskImpact || '',
              likelihoodRationale: risk.likelihoodRationale || '',
              inherentRiskLevel: determineRiskLevel(risk.residualRisk),
              likelihood: (() => {
                const calculated = risk.likelihoodScoreCalculated || risk.inherentProbability;
                console.log(`[Risk ${risk.riskId}] Likelihood - Calculated: ${risk.likelihoodScoreCalculated}, Inherent: ${risk.inherentProbability}, Using: ${calculated}`);
                return calculated;
              })(),
              impact: risk.impactScoreCalculated || risk.inherentImpact,
              likelihoodScoreCalculated: risk.likelihoodScoreCalculated,
              impactScoreCalculated: risk.impactScoreCalculated,
              finalRiskScoreCalculated: risk.finalRiskScoreCalculated,
              riskPriorityScore: risk.finalRiskScoreCalculated || risk.residualRisk,
              regulatoryTaxonomy: (() => {
                try {
                  const frameworks = typeof risk.regulatory_frameworks === 'string'
                    ? JSON.parse(risk.regulatory_frameworks)
                    : risk.regulatory_frameworks;
                  return Array.isArray(frameworks) ? frameworks.join(', ') : '';
                } catch (e) {
                  return '';
                }
              })(),
              businessTranslation: risk.businessTranslation || '',
              auditorMightAsk: risk.auditorMightAsk || '',
              keyEvidenceRequired: risk.keyEvidenceRequired || '',
              typicalControlCategories: (() => {
                try {
                  const controls = typeof risk.existing_controls === 'string'
                    ? JSON.parse(risk.existing_controls)
                    : risk.existing_controls;
                  if (Array.isArray(controls)) {
                    return controls.map((control: any) => control.type || control.control_type || control.controlType).filter(Boolean).join(', ');
                  }
                  return '';
                } catch (e) {
                  return '';
                }
              })(),
              businessImpactTranslation: risk.businessImpactTranslation || '',
              lastAssessed: risk.assessmentDate || new Date().toISOString().split('T')[0],
              assessedBy: risk.assessedBy || 'AI Risk Assessment',
              reviewStatus: risk.reviewStatus || 'pending',
              reviewDate: risk.reviewDate || null,
              aiGenerated: true,
              // Parse and add full controls array
              existing_controls: (() => {
                try {
                  const controls = typeof risk.existing_controls === 'string'
                    ? JSON.parse(risk.existing_controls)
                    : risk.existing_controls;
                  return Array.isArray(controls) ? controls : [];
                } catch (e) {
                  return [];
                }
              })(),
              // Parse and add framework violations from AI assessment
              frameworkViolations: (() => {
                try {
                  // Backend sends frameworkViolations (camelCase) after parsing
                  const violations = risk.frameworkViolations || risk.framework_violations;
                  const parsed = typeof violations === 'string'
                    ? JSON.parse(violations)
                    : violations;
                  console.log(`[Risk ${risk.riskId}] Framework Violations:`, parsed);
                  return Array.isArray(parsed) ? parsed : [];
                } catch (e) {
                  console.log(`[Risk ${risk.riskId}] Error parsing frameworkViolations:`, e);
                  return [];
                }
              })()
            };

            existingData.push(assessment);
          });
        });

        // Frontend safety filter: Exclude Start/End steps (defense-in-depth with backend filter)
        const filteredData = existingData.filter(assessment => {
          const stepName = assessment.stepName?.toLowerCase().trim() || '';
          return stepName !== 'start' && stepName !== 'end';
        });

        console.log(`✅ Loaded ${existingData.length} existing assessments (${filteredData.length} after filtering Start/End steps)`);
        return filteredData;
      }
    } catch (error) {
      console.error('Failed to load existing assessments:', error);
    }

    return [];
  }, [projectId]);

  // Clear all existing data first, then call individual steps one by one
  const performComprehensiveAssessment = useCallback(async () => {
    if (processMapSteps.length === 0 || !projectId) {
      console.log('❌ No process steps or project ID available for assessment');
      return;
    }

    setGeneratingAssessment(true);
    setRiskAssessmentData([]); // Clear UI immediately
    setPartialDataCleared(false); // Reset partial data flag

    try {
      console.log('🚀 Starting FRESH risk assessment for:', processMapSteps.length, 'process steps');
      console.log('🧹 Step 1: FORCE CLEAR all existing risk assessments (regeneration mode)');

      setGenerationProgress({
        current: 0,
        total: processMapSteps.length + 1, // +1 for the clearing step
        currentStep: 'Force clearing ALL existing risk assessments...',
        message: 'Ensuring complete data removal before fresh generation'
      });

      // STEP 1: FORCE Clear ALL existing risk assessments for this project (always, regardless of completeness)
      try {
        console.log('📡 Making DELETE request to clear all project data:', `/api/comprehensive-risk-assessment/project/${projectId}`);
        const deleteResponse = await api.delete(`/api/comprehensive-risk-assessment/project/${projectId}`);
        console.log('✅ DELETE request successful:', deleteResponse.data);
        console.log('✅ Successfully FORCE-cleared all existing risk assessments');
      } catch (clearError: any) {
        console.error('❌ CRITICAL: Clear operation failed:', clearError);
        console.error('❌ Clear error details:', clearError.response?.data || clearError.message);
        // Don't continue if we can't clear - this is critical for regeneration
        throw new Error(`Failed to clear existing data: ${clearError.response?.data?.message || clearError.message}`);
      }

      setGenerationProgress({
        current: 1,
        total: processMapSteps.length + 1,
        currentStep: 'Data cleared! Starting individual step generation...',
        message: 'Now generating fresh assessments for each process step'
      });

      // STEP 2: Call individual APIs for each step and load data progressively
      console.log('🔄 Step 2: Generating assessments for each process step individually');
      const assessments: RiskAssessmentRow[] = [];

      for (let i = 0; i < processMapSteps.length; i++) {
        const step = processMapSteps[i];

        setGenerationProgress({
          current: i + 2, // +2 because we already did the clear step
          total: processMapSteps.length + 1,
          currentStep: `Analyzing: ${step.name}`,
          message: `Processing step ${i + 1} of ${processMapSteps.length}`
        });

        try {
          console.log(`🔄 GENERATING NEW DATA for step ${i + 1}: ${step.name}`);
          console.log(`📡 Calling AI generation API with forceRegenerate=true`);

          const response = await api.post(`/api/comprehensive-risk-assessment/generate/${projectId}/${encodeURIComponent(step.name)}`, {
            processDescription: step.description || step.name,
            forceRegenerate: true // Force regenerate to ensure fresh AI-generated content
          });

          if (response.data?.success && response.data?.assessment) {
            console.log(`✨ NEW DATA GENERATED for ${step.name}:`, response.data.generated ? 'Fresh AI content' : 'Existing content returned');
            console.log(`📊 Assessment data:`, response.data.assessment);
            // Convert comprehensive risk assessment to table format
            const riskAssessment = response.data.assessment;
            if (riskAssessment.risks && riskAssessment.risks.length > 0) {
              // Convert each risk to table format
              riskAssessment.risks.forEach((risk: any, riskIndex: number) => {
                const assessment = convertComprehensiveRiskToTableFormat(risk, riskAssessment, i, step, riskIndex);
                if (assessment) {
                  assessments.push(assessment);
                }
              });
              console.log(`✅ Step ${i + 1} completed with ${riskAssessment.risks.length} risks`);

              // Update UI with current assessments in real-time (progressive loading)
              setRiskAssessmentData([...assessments]);
            }
          } else {
            console.warn(`⚠️ No valid data for step: ${step.name}`);
          }

        } catch (stepError: any) {
          console.error(`❌ Error processing step ${step.name}:`, stepError);
          // Continue with next step instead of failing entirely
        }

        // Small delay to prevent overwhelming the API
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      setGenerationProgress({
        current: processMapSteps.length + 1,
        total: processMapSteps.length + 1,
        currentStep: 'Assessment completed!',
        message: `Generated ${assessments.length} risk assessments`
      });

      console.log(`✅ Completed ${assessments.length} risk assessments`);

    } catch (error: any) {
      console.error('❌ Risk assessment failed:', error);
      setGenerationProgress({
        current: 0,
        total: processMapSteps.length + 1,
        currentStep: 'Error occurred',
        message: `Failed: ${error.response?.data?.error || error.message}`
      });

      alert(`Risk assessment failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setGeneratingAssessment(false);
      setTimeout(() => setGenerationProgress(null), 3000);
    }
  }, [processMapSteps, projectId]);

  // Background job version of comprehensive assessment
  const performComprehensiveAssessmentBackground = useCallback(async (scopingData?: any) => {
    if (processMapSteps.length === 0 || !projectId) {
      console.log('❌ No process steps or project ID available for assessment');
      return;
    }

    console.log('🔍 performComprehensiveAssessmentBackground called with scopingData:', scopingData);

    try {
      setGeneratingAssessment(true);

      // STEP 1: Refresh data first to ensure clean state (clear old cancelled job state)
      console.log('🔄 Refreshing data before starting new generation...');
      await loadExistingAssessments();
      console.log('✅ Data refreshed');

      // STEP 2: Clear UI data when user manually clicks Generate
      setRiskAssessmentData([]);
      console.log('🔄 Clearing UI data for fresh generation');

      setPartialDataCleared(false);

      // STEP 3: Start the background job
      console.log('🚀 Starting background risk assessment job for:', processMapSteps.length, 'process steps');
      console.log('📋 Process steps to be sent:', processMapSteps.map(step => step.name));

      // Use passed scopingData parameter (takes precedence) or fall back to state
      const riskIds = scopingData?.applicableRiskIds || scopingResult?.applicableRiskIds || null;
      console.log('🎯 Using applicableRiskIds:', riskIds);

      const response = await backgroundJobsAPI.startRiskAssessmentJob({
        projectId: parseInt(projectId.toString()),
        processSteps: processMapSteps.map((step, index) => ({
          name: step.name,
          description: step.description || step.name,
          processId: step.process_id || step.processId,
          // Don't send stepId - let backend generate based on array position
          // This preserves original numbering: Start=STEP_00, Merchant=STEP_01, etc.
          // SQL queries will filter Start/End from display
          stepId: undefined
        })),
        forceRegenerate: true, // ALWAYS force regenerate when user clicks Generate button
        applicableRiskIds: riskIds // Pass filtered risk IDs if scoping was done
      });

      console.log('✅ Job started with ID:', response.jobId, 'for', processMapSteps.length, 'steps');

      console.log('✅ Background job started:', response.jobId);
      setCurrentJobId(response.jobId);

      // Start polling for job status
      let lastProgressCurrent = 0;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5;
      const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes — matches ComprehensiveProcessRiskMapping
      const pollStartTime = Date.now();
      console.log('🔄 Starting new job polling with initial progress:', lastProgressCurrent);
      const polling = setInterval(async () => {
        // Hard timeout — if backend job never transitions out of pending, stop polling
        // and surface a clear error instead of hanging indefinitely.
        if (Date.now() - pollStartTime > POLL_TIMEOUT_MS) {
          clearInterval(polling);
          setJobPollingInterval(null);
          setCurrentJobId(null);
          setGeneratingAssessment(false);
          setGenerationProgress({
            current: 0,
            total: 0,
            currentStep: 'Timed out',
            message: 'Risk assessment timed out after 5 minutes. The background job did not complete — please try again.'
          });
          setTimeout(() => setGenerationProgress(null), 8000);
          return;
        }
        try {
          console.log('🔄 Polling job status...', response.jobId);
          const job = await backgroundJobsAPI.getJob(response.jobId);

          consecutiveErrors = 0; // reset on successful response
          if (!job) {
            console.error('❌ Job not found');
            clearInterval(polling);
            setJobPollingInterval(null);
            return;
          }

          setBackgroundJob(job);

          // Update progress display
          setGenerationProgress({
            current: job.progress.current,
            total: job.progress.total,
            currentStep: job.progress.currentStep || 'Processing...',
            message: job.progress.message || 'Working on your risk assessment...'
          });

          // Check if progress has increased (new step completed)
          console.log(`🔍 Progress check: current=${job.progress.current}, last=${lastProgressCurrent}, condition=${job.progress.current > lastProgressCurrent && job.progress.current > 1}`);

          if (job.progress.current > lastProgressCurrent && job.progress.current > 1) {
            console.log(`🔄 Progress updated: ${lastProgressCurrent} -> ${job.progress.current}, fetching new data...`);

            // Only fetch data if we're past the clearing step (progress > 1 means actual generation has started)
            if (job.progress.current > 1) {

            // Progress increment now happens AFTER data is saved, so no delay needed

            // Fetch and display newly generated risk assessment data
            try {
              console.log(`🔍 Current UI data count: ${riskAssessmentData.length}`);
              console.log(`🔍 About to call loadExistingAssessments() for project: ${projectId}`);
              const newData = await loadExistingAssessments();
              console.log(`📊 API returned ${newData ? newData.length : 0} assessments`);

              if (newData && newData.length > 0) {
                console.log(`🔄 Updating UI data from ${riskAssessmentData.length} to ${newData.length} assessments`);
                setRiskAssessmentData(newData);
                console.log(`✅ Refreshed data after step completion - loaded ${newData.length} assessments`);
              } else {
                console.warn(`⚠️ No data returned from loadExistingAssessments() - checking API response`);
                console.warn(`⚠️ Project ID: ${projectId}, Response structure:`, newData);
              }
            } catch (error) {
              console.error('❌ Error during loadExistingAssessments():', error);
            }

            }

            lastProgressCurrent = job.progress.current;
          } else if (lastProgressCurrent === 0) {
            // Initialize tracking
            lastProgressCurrent = job.progress.current;
          }

          // Check if job is complete
          if (['completed', 'failed', 'cancelled'].includes(job.status)) {
            console.log(`🏁 Job ${job.status} detected, stopping polling...`);
            clearInterval(polling);
            setJobPollingInterval(null);
            setCurrentJobId(null);

            if (job.status === 'completed') {
              console.log('✅ Background job completed successfully:', job.result);

              // IMPORTANT: Add a small delay to ensure DB transactions are fully committed
              await new Promise(resolve => setTimeout(resolve, 500));

              // Final refresh of the risk assessment data
              console.log('🔄 Fetching final assessment data after completion...');
              const finalData = await loadExistingAssessments();
              console.log(`📊 Final data fetch returned ${finalData?.length || 0} assessments`);

              if (finalData && finalData.length > 0) {
                setRiskAssessmentData(finalData);
                console.log(`✅ Updated UI with ${finalData.length} completed assessments`);
              } else {
                console.warn('⚠️ No data returned after job completion - retrying...');
                // Retry once after additional delay
                await new Promise(resolve => setTimeout(resolve, 1000));
                const retryData = await loadExistingAssessments();
                if (retryData && retryData.length > 0) {
                  setRiskAssessmentData(retryData);
                  console.log(`✅ Retry successful - loaded ${retryData.length} assessments`);
                }
              }

              setGenerationProgress({
                current: job.progress.total,
                total: job.progress.total,
                currentStep: 'Assessment completed!',
                message: `Successfully generated ${job.result?.successCount || 0} risk assessments`
              });

              const notification = document.createElement('div');
              notification.className = 'fixed top-4 right-4 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50';
              const successCount = String(job.result?.successCount || 0);
              notification.textContent = `Risk Assessment Complete! Generated ${successCount} assessments`;
              document.body.appendChild(notification);
              setTimeout(() => notification.remove(), 5000);

            } else if (job.status === 'failed') {
              setGenerationProgress({
                current: 0,
                total: job.progress.total || 1,
                currentStep: 'Job failed',
                message: `Error: ${job.error}`
              });

              const notification = document.createElement('div');
              notification.className = 'fixed top-4 right-4 bg-red-500 text-white p-4 rounded-lg shadow-lg z-50';
              notification.textContent = `Risk Assessment Failed: ${job.error || 'Unknown error occurred'}`;
              document.body.appendChild(notification);
              setTimeout(() => notification.remove(), 5000);
            } else if (job.status === 'cancelled') {
              console.warn('⚠️ Background job was cancelled');
            }

            // Update UI state
            setGeneratingAssessment(false);
            setTimeout(() => setGenerationProgress(null), 5000); // Show completion message longer
          }
        } catch (pollingError) {
          console.error('❌ Error polling job status:', pollingError);
          consecutiveErrors++;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            clearInterval(polling);
            setJobPollingInterval(null);
            setCurrentJobId(null);
            setGeneratingAssessment(false);
            // Surface a clear, human-readable error with actual progress context.
            // Do NOT auto-clear — the user needs to see this and act on it.
            // (Bug: previously used current:0/total:0 and cleared after 8s silently.)
            const _completed = lastProgressCurrent;
            const _total = processMapSteps.length;
            setGenerationProgress({
              current: _completed,
              total: _total,
              currentStep: 'Connection lost',
              message: `Lost connection while generating — ${_completed} of ${_total} step${_total !== 1 ? 's' : ''} completed. The server-side job may still be running. Try refreshing the page to resume.`,
              isError: true,
            });
            return;
          }
        }
      }, 1500); // Poll every 1.5 seconds (more responsive)

      setJobPollingInterval(polling);
      console.log('✅ Polling interval set for new job');

    } catch (error: any) {
      console.error('❌ Failed to start background job:', error);

      setGenerationProgress({
        current: 0,
        total: 1,
        currentStep: 'Failed to start',
        message: `Error: ${error.message}`
      });

      setGeneratingAssessment(false);
      setTimeout(() => setGenerationProgress(null), 3000);

      alert(`Failed to start background job: ${error.message}`);
    }
  }, [processMapSteps, projectId]);

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval);
      }
    };
  }, [jobPollingInterval]);

  // Cancel background job function
  const cancelBackgroundJob = useCallback(async () => {
    if (!currentJobId) {
      console.warn('⚠️ No current job ID to cancel');
      return;
    }

    // Show confirmation dialog
    setCancelConfirmDialog({
      open: true,
      title: 'Cancel Risk Assessment Generation?',
      message: 'This will permanently cancel the current job. You will need to start a fresh generation to continue. Any data generated so far will be preserved.',
      type: 'cancel',
      onConfirm: async () => {
        setCancelConfirmDialog({ ...cancelConfirmDialog, open: false });
        await performCancellation();
      }
    });
  }, [currentJobId]);

  // Perform the actual cancellation
  const performCancellation = useCallback(async () => {
    if (!currentJobId) return;

    try {
      console.log(`🛑 Attempting to cancel job: ${currentJobId}`);
      const success = await backgroundJobsAPI.cancelJob(currentJobId);
      console.log(`📝 Cancel API response: ${success}`);

      if (!success) {
        throw new Error('Failed to cancel job - API returned false');
      }

      // Stop polling
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval);
        setJobPollingInterval(null);
        console.log('⏹️ Stopped job polling interval');
      }

      // Verify job is actually cancelled
      try {
        const job = await backgroundJobsAPI.getJob(currentJobId);
        if (job && job.status !== 'cancelled') {
          console.warn(`⚠️ Job status is ${job.status}, not cancelled`);
          setCancelConfirmDialog({
            open: true,
            title: 'Warning: Job May Not Be Fully Cancelled',
            message: `Job status is ${job.status}, not cancelled. Please refresh and check.`,
            type: 'warning',
            onConfirm: () => setCancelConfirmDialog({ ...cancelConfirmDialog, open: false })
          });
        }
      } catch (verifyError) {
        console.warn('Could not verify job cancellation status:', verifyError);
      }

      // Clear job states
      setCurrentJobId(null);
      setBackgroundJob(null);
      setGeneratingAssessment(false);
      setGenerationProgress(null);

      // DO NOT clear the UI data - preserve any data generated so far
      // setRiskAssessmentData([]);  // REMOVED: Keep existing data
      setPartialDataCleared(false);

      console.log('✅ Background job cancelled successfully - preserving generated data');

      // Show success notification
      setCancelConfirmDialog({
        open: true,
        title: 'Risk Assessment Generation Cancelled',
        message: 'The job will not resume automatically. Any generated data has been preserved. Click "Generate Risk Assessment" to start a fresh generation.',
        type: 'success',
        onConfirm: () => setCancelConfirmDialog({ ...cancelConfirmDialog, open: false })
      });
    } catch (error: any) {
      console.error('❌ Failed to cancel job:', error);
      setCancelConfirmDialog({
        open: true,
        title: 'Failed to Cancel Job',
        message: error.message || 'An error occurred while cancelling the job.',
        type: 'warning',
        onConfirm: () => setCancelConfirmDialog({ ...cancelConfirmDialog, open: false })
      });
    }
  }, [currentJobId, jobPollingInterval]);

  // Load and initialize process steps from props
  useEffect(() => {
    initializeProcessSteps();
  }, [initializeProcessSteps]);

  // Load existing risk assessments when component mounts or projectId changes
  useEffect(() => {
    const loadDataOnMount = async () => {
      if (projectId && !generatingAssessment) {
        console.log('📂 Component mounted - checking for existing risk assessments...');
        setLoading(true);
        try {
          // First, check for any running background jobs for this project
          console.log('🔍 Checking for running background jobs...');
          const runningJobs = await backgroundJobsAPI.getJobs({
            projectId: parseInt(projectId.toString()),
            status: ['pending', 'running'],
            type: 'risk_assessment_generation'
          });

          if (runningJobs && runningJobs.length > 0) {
            const runningJob = runningJobs[0];
            console.log('🔄 Found running background job:', runningJob.id);

            // Resume the background job
            setCurrentJobId(runningJob.id);
            setBackgroundJob(runningJob);
            setGeneratingAssessment(true);

            // Start polling for this existing job
            let lastProgressCurrent = runningJob.progress?.current || 0;
            let resumeConsecutiveErrors = 0;
            const RESUME_MAX_ERRORS = 5;
            console.log('🔄 Resuming job with current progress:', lastProgressCurrent);
            const polling = setInterval(async () => {
              try {
                console.log('🔄 Polling resumed job status...', runningJob.id);
                const job = await backgroundJobsAPI.getJob(runningJob.id);
                resumeConsecutiveErrors = 0;

                if (!job) {
                  console.error('❌ Job not found');
                  clearInterval(polling);
                  setJobPollingInterval(null);
                  return;
                }

                setBackgroundJob(job);

                // Update progress display
                setGenerationProgress({
                  current: job.progress.current,
                  total: job.progress.total,
                  currentStep: job.progress.currentStep || 'Processing...',
                  message: job.progress.message || 'Working on your risk assessment...'
                });

                // Check if progress has increased (new step completed)
                if (job.progress.current > lastProgressCurrent && job.progress.current > 1) {
                  console.log(`🔄 Progress updated: ${lastProgressCurrent} -> ${job.progress.current}, fetching new data...`);

                  // Fetch and display newly generated risk assessment data
                  try {
                    const newData = await loadExistingAssessments();
                    if (newData && newData.length > 0) {
                      setRiskAssessmentData(newData);
                      console.log(`✅ Refreshed data after step completion - loaded ${newData.length} assessments`);
                    }
                  } catch (error) {
                    console.warn('⚠️ Failed to refresh data during background job:', error);
                  }

                  lastProgressCurrent = job.progress.current;
                } else if (lastProgressCurrent === 0) {
                  // Initialize tracking
                  lastProgressCurrent = job.progress.current;
                }

                // Check if job is complete
                if (['completed', 'failed', 'cancelled'].includes(job.status)) {
                  console.log(`🏁 Resumed job ${job.status} detected, stopping polling...`);
                  clearInterval(polling);
                  setJobPollingInterval(null);
                  setCurrentJobId(null);

                  if (job.status === 'completed') {
                    console.log('✅ Background job completed successfully:', job.result);

                    // IMPORTANT: Add a small delay to ensure DB transactions are fully committed
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Final refresh of the risk assessment data
                    console.log('🔄 Fetching final assessment data after completion...');
                    const finalData = await loadExistingAssessments();
                    console.log(`📊 Final data fetch returned ${finalData?.length || 0} assessments`);

                    if (finalData && finalData.length > 0) {
                      setRiskAssessmentData(finalData);
                      console.log(`✅ Updated UI with ${finalData.length} completed assessments`);
                    } else {
                      console.warn('⚠️ No data returned after job completion - retrying...');
                      // Retry once after additional delay
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      const retryData = await loadExistingAssessments();
                      if (retryData && retryData.length > 0) {
                        setRiskAssessmentData(retryData);
                        console.log(`✅ Retry successful - loaded ${retryData.length} assessments`);
                      }
                    }

                    setGenerationProgress({
                      current: job.progress.total,
                      total: job.progress.total,
                      currentStep: 'Assessment completed!',
                      message: `Successfully generated ${job.result?.successCount || 0} risk assessments`
                    });

                    const notification = document.createElement('div');
                    notification.className = 'fixed top-4 right-4 bg-green-500 text-white p-4 rounded-lg shadow-lg z-50';
                    const resumeCount = String(job.result?.successCount || 0);
                    notification.textContent = `Risk Assessment Complete! Generated ${resumeCount} assessments`;
                    document.body.appendChild(notification);
                    setTimeout(() => notification.remove(), 5000);

                    // Clear progress after showing completion message
                    setTimeout(() => setGenerationProgress(null), 5000);
                  }

                  setGeneratingAssessment(false);
                }
              } catch (error) {
                console.error('❌ Error polling resumed job:', error);
                resumeConsecutiveErrors++;
                if (resumeConsecutiveErrors >= RESUME_MAX_ERRORS) {
                  clearInterval(polling);
                  setJobPollingInterval(null);
                  setCurrentJobId(null);
                  setGeneratingAssessment(false);
                  const _completed = lastProgressCurrent;
                  const _total = runningJob.progress?.total || 0;
                  setGenerationProgress({
                    current: _completed,
                    total: _total,
                    currentStep: 'Connection lost',
                    message: `Lost connection while generating — ${_completed} of ${_total} step${_total !== 1 ? 's' : ''} completed. The server-side job may still be running. Try refreshing the page to resume.`,
                    isError: true,
                  });
                }
              }
            }, 1500); // Poll every 1.5 seconds (more responsive)

            setJobPollingInterval(polling);
            console.log('✅ Polling interval set for resumed job');

            // Load existing data and exit early
            const existingAssessments = await loadExistingAssessments();
            if (existingAssessments && existingAssessments.length > 0) {
              setRiskAssessmentData(existingAssessments);
            }
            setLoading(false);
            return;
          }

          // No running jobs, proceed with normal data loading
          const existingAssessments = await loadExistingAssessments();

          // Only clear data if it's genuinely problematic, not just because it's "partial"
          // Check for recent generation activity that might have been interrupted by browser refresh
          const recentGeneration = existingAssessments.some(assessment => {
            const assessmentDate = new Date(assessment.lastAssessed);
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000); // Reduced from 10 to 5 minutes
            return assessmentDate > fiveMinutesAgo;
          });

          // Check if this looks like genuinely interrupted data (very recent + very few entries)
          const hasPartialData = existingAssessments.length > 0 && existingAssessments.length < processMapSteps.length;
          const likelyInterrupted = hasPartialData && recentGeneration && existingAssessments.length < 3; // Only if very few entries

          if (likelyInterrupted) {
            console.warn('⚠️ Detected likely interrupted generation (very recent + very few entries)');
            console.log(`Found only ${existingAssessments.length} assessments with very recent timestamps - likely browser refresh during generation`);
            console.log('🧹 Auto-clearing interrupted data to ensure fresh regeneration');

            // Clear the data from database
            try {
              console.log('📡 Making DELETE request to clear interrupted data');
              const deleteResponse = await api.delete(`/api/comprehensive-risk-assessment/project/${projectId}`);
              console.log('✅ DELETE request successful:', deleteResponse.data);
              console.log('✅ Cleared interrupted risk assessment data');
            } catch (clearError) {
              console.warn('⚠️ Failed to clear interrupted data:', clearError);
            }

            // Show empty state and suggest regeneration
            setRiskAssessmentData([]);
            setPartialDataCleared(true);
            console.log('🔄 Ready for fresh generation - interrupted data cleared');

          } else if (existingAssessments.length > 0) {
            // Load existing data, whether partial or complete - preserve what user has
            setRiskAssessmentData(existingAssessments);
            console.log(`✅ Loaded ${existingAssessments.length} existing risk assessments on page load`);

            if (hasPartialData && !recentGeneration) {
              console.log(`ℹ️ Note: Found ${existingAssessments.length} assessments for ${processMapSteps.length} process steps (partial data preserved)`);
            }

          } else {
            console.log('ℹ️ No existing assessments found - table will be empty until generation');
            setRiskAssessmentData([]);
          }
        } catch (error) {
          console.error('❌ Failed to load existing assessments on mount:', error);
          setRiskAssessmentData([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadDataOnMount();
  }, [projectId, loadExistingAssessments, processMapSteps.length]); // Add processMapSteps.length to dependencies
 
  // Filtering logic
  useEffect(() => {
    let filtered = riskAssessmentData || [];

    // Search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        item.stepName.toLowerCase().includes(searchLower) ||
        item.l4Risk.toLowerCase().includes(searchLower) ||
        item.rationale.toLowerCase().includes(searchLower)
      );
    }

    // Risk level filter
    if (filters.riskLevel !== 'all') {
      filtered = filtered.filter(item => item.inherentRiskLevel === filters.riskLevel);
    }

    // Taxonomy filter
    if (filters.taxonomy !== 'all') {
      filtered = filtered.filter(item => item.regulatoryTaxonomy.includes(filters.taxonomy));
    }

    // Priority score filter
    if (filters.priorityScore !== 'all') {
      if (filters.priorityScore === 'high') {
        filtered = filtered.filter(item => item.riskPriorityScore >= 15);
      } else if (filters.priorityScore === 'medium') {
        filtered = filtered.filter(item => item.riskPriorityScore >= 9 && item.riskPriorityScore < 15);
      } else if (filters.priorityScore === 'low') {
        filtered = filtered.filter(item => item.riskPriorityScore < 9);
      }
    }

    setFilteredData(filtered);
  }, [filters, riskAssessmentData]);

  // Group risks by process step
  const groupedData = useMemo(() => {
    const grouped = new Map<string, {
      stepInfo: {
        processId: string;
        processStepId: string;
        stepName: string;
      };
      risks: RiskAssessmentRow[];
      totalRisks: number;
      highestPriority: number;
      criticalCount: number;
      highCount: number;
      mediumCount: number;
      lowCount: number;
    }>();

    filteredData.forEach(row => {
      const key = `${row.processId}-${row.processStepId}`;
      
      if (!grouped.has(key)) {
        grouped.set(key, {
          stepInfo: {
            processId: row.processId,
            processStepId: row.processStepId,
            stepName: row.stepName
          },
          risks: [],
          totalRisks: 0,
          highestPriority: 0,
          criticalCount: 0,
          highCount: 0,
          mediumCount: 0,
          lowCount: 0
        });
      }

      const group = grouped.get(key)!;
      group.risks.push(row);
      group.totalRisks++;
      group.highestPriority = Math.max(group.highestPriority, row.riskPriorityScore);
      
      // Count risk levels
      switch (row.inherentRiskLevel) {
        case 'CRITICAL':
          group.criticalCount++;
          break;
        case 'HIGH':
          group.highCount++;
          break;
        case 'MEDIUM':
          group.mediumCount++;
          break;
        case 'LOW':
        case 'VERY LOW':
          group.lowCount++;
          break;
      }
    });

    return Array.from(grouped.values());
  }, [filteredData]);

  // Sorting logic for grouped data
  const sortedGroupedData = useMemo(() => {
    return [...groupedData].sort((a, b) => {
      // Sort by process step ID with numerical ordering
      if (sortConfig.key === 'processStepId' || !sortConfig.key) {
        // Extract numerical part from step IDs like "PROC...01", "PROC...10"
        const extractNumber = (id: string) => {
          const match = id.match(/(\d+)$/);
          return match ? parseInt(match[1], 10) : 0;
        };

        const aNum = extractNumber(a.stepInfo.processStepId);
        const bNum = extractNumber(b.stepInfo.processStepId);

        return sortConfig.direction === 'asc' ?
          aNum - bNum :
          bNum - aNum;
      }

      // Sort by highest priority risk score
      if (sortConfig.key === 'riskPriorityScore') {
        return sortConfig.direction === 'desc' ?
          b.highestPriority - a.highestPriority :
          a.highestPriority - b.highestPriority;
      }

      // Sort by step name
      if (sortConfig.key === 'stepName') {
        const aVal = a.stepInfo.stepName.toLowerCase();
        const bVal = b.stepInfo.stepName.toLowerCase();
        return sortConfig.direction === 'asc' ?
          aVal.localeCompare(bVal) :
          bVal.localeCompare(aVal);
      }

      // Sort by process ID
      if (sortConfig.key === 'processId') {
        const aVal = a.stepInfo.processId.toLowerCase();
        const bVal = b.stepInfo.processId.toLowerCase();
        return sortConfig.direction === 'asc' ?
          aVal.localeCompare(bVal) :
          bVal.localeCompare(aVal);
      }

      return 0;
    });
  }, [groupedData, sortConfig]);

  // Event handlers
  const handleSort = (key: keyof RiskAssessmentRow) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectRow = (id: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allRiskIds = sortedGroupedData.flatMap(group => group.risks.map(risk => risk.id));
    if (selectedRows.size === allRiskIds.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(allRiskIds));
    }
  };

  const toggleRowExpansion = (id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleProcessStepExpansion = (processStepKey: string) => {
    setExpandedProcessSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(processStepKey)) {
        newSet.delete(processStepKey);
      } else {
        newSet.add(processStepKey);
      }
      return newSet;
    });
  };

  // Edit mode handlers
  const handleStartEdit = (row: RiskAssessmentRow) => {
    setEditingRowId(row.id);
    setEditedValues({
      likelihood: row.likelihood,
      impact: row.impact,
      rationale: row.rationale,
      regulatoryTaxonomy: row.regulatoryTaxonomy,
      businessTranslation: row.businessTranslation,
      auditorMightAsk: row.auditorMightAsk,
      keyEvidenceRequired: row.keyEvidenceRequired,
      businessImpactTranslation: row.businessImpactTranslation
    });

    // Automatically expand the row if it's not already expanded
    if (!expandedRows.has(row.id)) {
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.add(row.id);
        return newSet;
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditedValues({});
  };

  const handleViewAuditTrail = (row: RiskAssessmentRow) => {
    setSelectedRiskForAudit({ id: row.id, riskId: row.riskId });
    setAuditTrailModalOpen(true);
  };

  const handleCloseAuditTrail = () => {
    setAuditTrailModalOpen(false);
    setSelectedRiskForAudit(null);
  };

  // Risk Scoring Modal Handlers
  const handleOpenRiskScoring = (row: RiskAssessmentRow) => {
    setSelectedRiskForScoring({
      riskId: row.riskId,
      processStepName: row.stepName,
      likelihood: row.likelihood,
      impact: row.impact,
      controlEffectiveness: 3 // Default, can be enhanced later
    });
    setScoringModalOpen(true);
  };

  const handleCloseRiskScoring = () => {
    setScoringModalOpen(false);
    setSelectedRiskForScoring(null);
  };

  const handleScoringComplete = async () => {
    // Refresh data after scoring is saved
    if (projectId) {
      const refreshedData = await loadExistingAssessments();
      if (refreshedData && refreshedData.length > 0) {
        setRiskAssessmentData(refreshedData);
        console.log('✅ Risk assessment data refreshed after scoring');
      }
    }
  };

  const handleFieldChange = (field: keyof RiskAssessmentRow, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRationaleValidation = async (rowId: number, status: 'approved' | 'rejected') => {
    try {
      setLoading(true);

      const updatedData: any = {
        rationale_validation_status: status,
        rationale_validated_by: 'Current User', // TODO: Get from auth context
        rationale_validated_at: new Date().toISOString()
      };

      await api.post(`/api/comprehensive-risk-assessment/review/${rowId}`, {
        reviewType: 'rationale_validation',
        approvalStatus: status,
        reviewComments: `Risk Statement ${status}`,
        updatedValues: updatedData
      });

      // Update local state
      setRiskAssessmentData(prev =>
        prev.map(row => {
          if (row.id === rowId) {
            return {
              ...row,
              rationaleValidationStatus: status,
              rationaleValidatedBy: 'Current User',
              rationaleValidatedAt: new Date().toISOString()
            };
          }
          return row;
        })
      );

      console.log(`Risk Statement ${status} successfully`);
    } catch (error) {
      console.error('Failed to validate risk statement:', error);
      alert('Failed to save validation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async (rowId: number) => {
    try {
      setLoading(true);

      // Map frontend field names to database column names
      const updatedData: any = {};

      // Note: Likelihood and Impact scores are not editable here
      // They must be edited via the Risk Scoring Modal for proper audit trail

      // Map other fields to database column names
      if (editedValues.rationale !== undefined) {
        updatedData.risk_statement = editedValues.rationale;
      }
      if (editedValues.businessTranslation !== undefined) {
        updatedData.business_translation = editedValues.businessTranslation;
      }
      if (editedValues.auditorMightAsk !== undefined) {
        updatedData.auditor_might_ask = editedValues.auditorMightAsk;
      }
      if (editedValues.keyEvidenceRequired !== undefined) {
        updatedData.key_evidence_required = editedValues.keyEvidenceRequired;
      }
      if (editedValues.businessImpactTranslation !== undefined) {
        updatedData.business_impact_translation = editedValues.businessImpactTranslation;
      }
      // Note: regulatoryTaxonomy is derived from regulatory_frameworks JSONB field
      // For now, we don't support editing it directly as it requires JSONB manipulation
      if (editedValues.regulatoryTaxonomy !== undefined) {
        // Convert comma-separated string to JSONB array
        const frameworks = editedValues.regulatoryTaxonomy
          .split(',')
          .map((f: string) => f.trim())
          .filter((f: string) => f.length > 0);
        updatedData.regulatory_frameworks = JSON.stringify(frameworks);
      }

      // Call the review endpoint to update the risk assessment
      await api.post(`/api/comprehensive-risk-assessment/review/${rowId}`, {
        reviewType: 'risk_edit',
        approvalStatus: 'approved',
        reviewComments: 'Updated via edit interface',
        updatedValues: updatedData
      });

      // Update local state (excluding likelihood and impact which are managed via Risk Scoring Modal)
      setRiskAssessmentData(prev =>
        prev.map(row => {
          if (row.id === rowId) {
            return {
              ...row,
              ...editedValues,
              // Update review metadata to show "edited" indicator
              reviewDate: new Date().toISOString(),
              // If Risk Statement (rationale) was edited, reset validation status to pending
              rationaleValidationStatus: editedValues.rationale !== undefined ? 'pending' : row.rationaleValidationStatus
              // Note: likelihood, impact, and riskPriorityScore are not updated here
              // They are managed through the Risk Scoring Modal
            };
          }
          return row;
        })
      );

      // Clear edit mode
      setEditingRowId(null);
      setEditedValues({});

      console.log('Risk assessment updated successfully');
    } catch (error) {
      console.error('Failed to save risk assessment:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    initializeProcessSteps();
    // Clear existing risk assessments and wait for user to generate new ones
    setRiskAssessmentData([]);
    setFilteredData([]);
    setLoading(false);
  };


  const getRiskLevelColor = (level: string): string => {
    switch (level) {
      case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-800 border-green-200';
      case 'VERY LOW': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSortIcon = (key: keyof RiskAssessmentRow) => {
    if (sortConfig.key !== key) return <ChevronDown className="w-4 h-4 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  // Helper function to truncate and format long IDs - shows ending part like STEP_01
  const formatId = (id: string | undefined | null, maxLength: number = 15): { short: string; full: string } => {
    // Handle undefined, null, or empty strings
    if (!id || typeof id !== 'string') {
      return { short: 'N/A', full: 'N/A' };
    }

    if (id.length <= maxLength) {
      return { short: id, full: id };
    }

    // Try to extract meaningful parts from the ID
    const parts = id.split('_');
    if (parts.length > 1) {
      // For step IDs, show the last 2 parts (e.g., STEP_01)
      const lastTwo = parts.slice(-2).join('_');
      if (lastTwo.length + 3 <= maxLength) {
        return {
          short: `...${lastTwo}`,
          full: id
        };
      }
    }

    // Fallback to showing end of the string
    return {
      short: `...${id.substring(id.length - (maxLength - 3))}`,
      full: id
    };
  };

  // Helper function to format step names with better wrapping
  const formatStepName = (name: string | undefined | null, maxLength: number = 50): { display: string; full: string } => {
    // Handle undefined, null, or empty strings
    if (!name || typeof name !== 'string') {
      return { display: 'N/A', full: 'N/A' };
    }
    
    if (name.length <= maxLength) {
      return { display: name, full: name };
    }
    
    // Try to break at natural boundaries
    const words = name.split(' ');
    let truncated = '';
    for (const word of words) {
      if ((truncated + word + ' ').length <= maxLength - 3) {
        truncated += word + ' ';
      } else {
        break;
      }
    }
    
    return {
      display: truncated.trim() + '...',
      full: name
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading risk assessment data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Interrupted Generation Alert */}
      {partialDataCleared && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <RefreshCw className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h4 className="font-semibold text-orange-800">Interrupted Generation Detected & Cleared</h4>
              <p className="text-sm text-orange-700">
                Browser refresh interrupted the previous risk generation very early in the process. The incomplete data has been automatically cleared to ensure fresh, complete assessment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Summary Panel */}
      {riskAssessmentData.length > 0 && (
        <RiskSummaryPanel summary={riskSummary} loading={summaryLoading} />
      )}

      {/* Risk Assessment Table Info with Action Buttons */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900">Risk Assessment Table</h3>
            <p className="text-sm text-gray-600">
              Single risk assessment per process step with comprehensive regulatory analysis ({riskAssessmentData.length} assessments)
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRiskScopingModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#9D7AB8] text-white rounded-lg hover:bg-[#454F83] transition-colors"
            >
              <Settings className="h-4 w-4" />
              Risk Scoping
            </button>
            <button
              onClick={() => setShowQuestionnaireModal(true)}
              disabled={generatingAssessment || processMapSteps.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              <Brain className={`h-4 w-4 ${generatingAssessment ? 'animate-spin' : ''}`} />
              {generatingAssessment
                ? 'Generating...'
                : (riskAssessmentData.length > 0 ? 'Regenerate All Assessments' : 'Generate Risk Assessments')
              }
            </button>
            <button
              onClick={onRefresh}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Data
            </button>
            <button
              onClick={() => onExport("excel")}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#221551] text-white rounded-lg hover:bg-[#454F83] transition-colors"
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Progress Indicator */}
      {generationProgress && (
        <div className={`p-4 rounded-lg border ${generationProgress.isError ? 'bg-red-50 border-red-200' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className={`h-5 w-5 ${generationProgress.isError ? 'text-red-600' : 'text-purple-600 animate-pulse'}`} />
              <span className={`font-semibold ${generationProgress.isError ? 'text-red-800' : 'text-purple-800'}`}>
                {generationProgress.isError ? 'Generation Interrupted' : 'Risk Assessment in Progress'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className={`text-sm ${generationProgress.isError ? 'text-red-600' : 'text-purple-600'}`}>
                {generationProgress.current}/{generationProgress.total}
              </div>
              {currentJobId && (
                <button
                  onClick={cancelBackgroundJob}
                  className="text-xs px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors font-medium flex items-center gap-1.5"
                  title="Permanently stop the risk assessment generation"
                >
                  <X className="h-3 w-3" />
                  Cancel Job
                </button>
              )}
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${generationProgress.isError ? 'bg-red-400' : 'bg-gradient-to-r from-purple-500 to-blue-500'}`}
              style={{ width: `${generationProgress.total > 0 ? (generationProgress.current / generationProgress.total) * 100 : 0}%` }}
            />
          </div>

          <div className="text-sm text-gray-700">
            <div className={`font-medium ${generationProgress.isError ? 'text-red-700' : ''}`}>{generationProgress.currentStep}</div>
            <div className="text-gray-600">{generationProgress.message}</div>
            {!generationProgress.isError && currentJobId && (
              <div className="text-xs text-purple-600 mt-1">
                Assessment in progress • You can safely navigate to other pages
              </div>
            )}
          </div>

          {backgroundJob && !generationProgress.isError && (
            <div className="mt-3 p-2 bg-white rounded border text-xs">
              <div className="flex justify-between items-center">
                <span>Status: <strong className="capitalize">{backgroundJob.status}</strong></span>
                <span>{Math.round(backgroundJob.progress.percentage)}% Complete</span>
              </div>
              {backgroundJob.result && backgroundJob.status === 'completed' && (
                <div className="text-green-600 mt-1">
                  ✅ Successfully generated {backgroundJob.result.successCount} assessments
                  {backgroundJob.result.errorCount > 0 && (
                    <span className="text-orange-600"> ({backgroundJob.result.errorCount} errors)</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search risks..."
              value={filters.searchTerm}
              onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg w-full text-sm"
            />
          </div>
          
          <select
            value={filters.riskLevel}
            onChange={(e) => setFilters(prev => ({ ...prev, riskLevel: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Risk Levels</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <select
            value={filters.taxonomy}
            onChange={(e) => setFilters(prev => ({ ...prev, taxonomy: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Frameworks</option>
            <option value="Basel">Basel Framework</option>
            <option value="SOX">SOX</option>
            <option value="COSO">COSO</option>
            <option value="ISO">ISO</option>
            <option value="GDPR">GDPR</option>
          </select>

          <select
            value={filters.priorityScore}
            onChange={(e) => setFilters(prev => ({ ...prev, priorityScore: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Priorities</option>
            <option value="high">High Priority (15+)</option>
            <option value="medium">Medium Priority (9-14)</option>
            <option value="low">Low Priority (&lt;9)</option>
          </select>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedRows.size > 0 ? `${selectedRows.size} risks selected` : `${sortedGroupedData.length} process steps, ${sortedGroupedData.reduce((total, group) => total + group.totalRisks, 0)} total risks`}
            </span>
          </div>
        </div>
      </div>

      {/* Risk Assessment Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="w-12 p-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === sortedGroupedData.reduce((total, group) => total + group.totalRisks, 0) && sortedGroupedData.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="w-32 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('processId')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Process ID</span>
                    {getSortIcon('processId')}
                  </div>
                </th>
                <th className="w-28 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('processStepId')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Step ID</span>
                    {getSortIcon('processStepId')}
                  </div>
                </th>
                <th className="w-80 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('stepName')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Step Name</span>
                    {getSortIcon('stepName')}
                  </div>
                </th>
                <th className="w-24 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('riskId')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Risk ID</span>
                    {getSortIcon('riskId')}
                  </div>
                </th>
                <th className="w-40 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('l4Risk')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">L4 Risk</span>
                    {getSortIcon('l4Risk')}
                  </div>
                </th>

                {/* Common Columns */}
                <th className="w-24 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('inherentRiskLevel')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Risk Level</span>
                    {getSortIcon('inherentRiskLevel')}
                  </div>
                </th>
                <th className="w-20 text-center p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('likelihood')}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Likelihood</span>
                    {getSortIcon('likelihood')}
                  </div>
                </th>
                <th className="w-20 text-center p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('impact')}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Impact</span>
                    {getSortIcon('impact')}
                  </div>
                </th>
                <th className="w-24 text-center p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('riskPriorityScore')}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Priority Score</span>
                    {getSortIcon('riskPriorityScore')}
                  </div>
                </th>
                <th className="w-36 text-left p-3 font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => handleSort('regulatoryTaxonomy')}>
                  <div className="flex items-center gap-1">
                    <span className="text-xs uppercase tracking-wide">Regulatory Framework</span>
                    {getSortIcon('regulatoryTaxonomy')}
                  </div>
                </th>
                <th className="w-40 text-center p-3 font-semibold text-gray-900">
                  <span className="text-xs uppercase tracking-wide">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sortedGroupedData.map((group) => {
                const processStepKey = `${group.stepInfo.processId}-${group.stepInfo.processStepId}`;
                const isExpanded = expandedProcessSteps.has(processStepKey);
                const groupRisks = group.risks.sort((a, b) => b.riskPriorityScore - a.riskPriorityScore);
                
                return (
                  <React.Fragment key={processStepKey}>
                    {/* Process Step Summary Row */}
                    <tr className="bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border-b-2 border-blue-200 transition-colors">
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={group.risks.every(risk => selectedRows.has(risk.id))}
                          onChange={() => {
                            const allSelected = group.risks.every(risk => selectedRows.has(risk.id));
                            const newSelection = new Set(selectedRows);
                            group.risks.forEach(risk => {
                              if (allSelected) {
                                newSelection.delete(risk.id);
                              } else {
                                newSelection.add(risk.id);
                              }
                            });
                            setSelectedRows(newSelection);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="p-3">
                        <div 
                          className="text-sm font-bold text-blue-700 truncate cursor-help" 
                          title={group.stepInfo.processId}
                        >
                          {group.stepInfo.processId}
                          {/* {formatId(group.stepInfo.processId, 20).short} */}
                        </div>
                      </td>
                      <td className="p-3">
                        <div 
                          className="text-sm font-bold text-blue-700 truncate cursor-help" 
                          title={group.stepInfo.processStepId}
                        >
                          {formatId(group.stepInfo.processStepId, 15).short}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleProcessStepExpansion(processStepKey)}
                            className="flex items-center gap-2 text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors group"
                            title={group.stepInfo.stepName}
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-blue-600" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-blue-600" />
                            )}
                            <div className="text-left leading-tight">
                              <div className="font-semibold text-gray-900 group-hover:text-blue-700">
                                {formatStepName(group.stepInfo.stepName, 60).display}
                              </div>
                              <div className="text-xs text-blue-600 font-medium">
                                {group.totalRisks} risk{group.totalRisks !== 1 ? 's' : ''} • Click to {isExpanded ? 'collapse' : 'expand'}
                              </div>
                            </div>
                          </button>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-gray-500 text-center">
                          Multiple
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-gray-500 truncate" title="Multiple Risk Types">
                          {group.risks.length > 1 ? 'Multiple' : (group.risks[0]?.l4Risk ? formatStepName(group.risks[0].l4Risk, 15).display : 'N/A')}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="text-xs text-gray-500 truncate" title="Various Regulatory Frameworks">
                          {group.risks.length > 1 ? 'Various' : (group.risks[0]?.regulatoryTaxonomy ? formatStepName(group.risks[0].regulatoryTaxonomy, 15).display : 'N/A')}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {group.criticalCount > 0 && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium border border-red-200">
                              {group.criticalCount} Critical
                            </span>
                          )}
                          {group.highCount > 0 && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-medium border border-orange-200">
                              {group.highCount} High
                            </span>
                          )}
                          {group.mediumCount > 0 && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full font-medium border border-yellow-200">
                              {group.mediumCount} Medium
                            </span>
                          )}
                          {group.lowCount > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium border border-green-200">
                              {group.lowCount} Low
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="text-xs text-gray-600">
                          {Math.min(...group.risks.map(r => r.likelihood))}-{Math.max(...group.risks.map(r => r.likelihood))}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="text-xs text-gray-600">
                          {Math.min(...group.risks.map(r => r.impact))}-{Math.max(...group.risks.map(r => r.impact))}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center">
                          <span className={`text-lg font-bold px-2 py-1 rounded-lg ${
                            group.highestPriority >= 20 ? 'text-red-700 bg-red-50' :
                            group.highestPriority >= 15 ? 'text-orange-700 bg-orange-50' :
                            group.highestPriority >= 9 ? 'text-yellow-700 bg-yellow-50' :
                            'text-green-700 bg-green-50'
                          }`}>
                            {group.highestPriority}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => toggleProcessStepExpansion(processStepKey)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
                            title={isExpanded ? "Collapse risks" : "Expand risks"}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Individual Risk Rows (when expanded) */}
                    {isExpanded && groupRisks.map((row, index) => (
                      <React.Fragment key={row.id}>
                        <tr className="bg-gradient-to-r from-gray-50 to-gray-100 hover:from-white hover:to-gray-50 border-l-4 border-gray-300 transition-colors">
                          <td className="p-3 pl-8">
                            <input
                              type="checkbox"
                              checked={selectedRows.has(row.id)}
                              onChange={() => handleSelectRow(row.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="p-3">
                            <div 
                              className="text-sm text-blue-600 opacity-70 truncate cursor-help font-mono"
                              title={row.processId}
                            >
                              {formatId(row.processId, 15).short}
                            </div>
                          </td>
                          <td className="p-3">
                            <div 
                              className="text-sm text-blue-600 opacity-70 truncate cursor-help font-mono"
                              title={row.processStepId}
                            >
                              {formatId(row.processStepId, 12).short}
                            </div>
                          </td>
                          <td className="p-3 pl-8">
                            <div 
                              className="text-sm text-gray-700 leading-tight cursor-help"
                              title={row.stepName}
                            >
                              {formatStepName(row.stepName, 40).display}
                            </div>
                          </td>
                          <td className="p-3">
                            <div
                              className="text-sm font-mono text-purple-600 truncate cursor-help bg-purple-50 px-2 py-1 rounded"
                              title={row.riskId}
                            >
                              {row.riskId ? row.riskId.split('_').slice(-2).join('_') : row.riskId}
                            </div>
                          </td>
                          <td className="p-3">
                            <div 
                              className="text-sm text-gray-900 leading-tight cursor-help"
                              title={row.l4Risk || 'No Risk Name'}
                            >
                              {row.l4Risk ? formatStepName(row.l4Risk, 35).display : '-'}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border shadow-sm ${getRiskLevelColor(row.inherentRiskLevel)}`}>
                              {row.inherentRiskLevel}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center bg-blue-50 px-2 py-1 rounded">
                              <span className="text-sm font-medium text-blue-700">{row.likelihood}</span>
                              <span className="text-xs text-blue-500 ml-1">/5</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center bg-orange-50 px-2 py-1 rounded">
                              <span className="text-sm font-medium text-orange-700">{row.impact}</span>
                              <span className="text-xs text-orange-500 ml-1">/5</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center bg-gray-100 px-2 py-1 rounded shadow-sm">
                              <span className={`text-lg font-bold ${
                                row.riskPriorityScore >= 20 ? 'text-red-600' :
                                row.riskPriorityScore >= 15 ? 'text-orange-600' :
                                row.riskPriorityScore >= 9 ? 'text-yellow-600' :
                                'text-green-600'
                              }`}>
                                {row.riskPriorityScore}
                              </span>
                              <span className="text-xs text-gray-500 ml-1">/25</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="text-sm text-gray-700">
                              {row.regulatoryTaxonomy || <span className="text-gray-400 italic text-xs">No frameworks identified for this risk</span>}
                            </div>
                          </td>
                          <td className="p-3 w-40">
                            <div className="flex items-center justify-center gap-1.5 flex-wrap">
                              <button
                                onClick={() => toggleRowExpansion(row.id)}
                                className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors border border-blue-200"
                                title={expandedRows.has(row.id) ? "Hide Full Risk Details" : "View Full Risk Details"}
                              >
                                {expandedRows.has(row.id) ? "Hide Details" : "View Risk Statement"}
                              </button>
                              {onRiskSelect && (
                                <button
                                  onClick={() => onRiskSelect(row.id)}
                                  className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="Calculate Residual Risk"
                                >
                                  <Calculator className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleViewAuditTrail(row)}
                                className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors relative"
                                title="View History"
                              >
                                <Clock className="h-4 w-4" />
                                {row.reviewDate !== null && row.reviewDate !== undefined && (
                                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full border border-white" title="Has been edited" />
                                )}
                              </button>
                              <div className={`w-3 h-3 rounded-full border-2 border-white shadow-sm ${
                                row.rationaleValidationStatus === 'approved' ? 'bg-green-500' :
                                row.rationaleValidationStatus === 'rejected' ? 'bg-red-500' :
                                'bg-yellow-500'
                              }`} title={`Risk Statement: ${row.rationaleValidationStatus || 'pending'}`} />
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expanded Row Details */}
                        {expandedRows.has(row.id) && (
                          <tr>
                            <td colSpan={12} className="p-0">
                              <div className="bg-gradient-to-br from-gray-50 to-slate-50 border-t-2 border-blue-200">
                                <div className="p-8 space-y-8">
                                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                    {/* Risk Assessment Details - Takes 2 columns */}
                                    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                                      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                                        <div className="flex items-center gap-3">
                                          <div className="p-2 bg-blue-100 rounded-lg">
                                            <Target className="h-5 w-5 text-blue-600" />
                                          </div>
                                          <h4 className="text-lg font-semibold text-gray-900">Risk Assessment Details</h4>
                                        </div>
                                        {editingRowId === row.id ? (
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => handleSaveEdit(row.id)}
                                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
                                            >
                                              <CheckCircle className="h-4 w-4" />
                                              Save
                                            </button>
                                            <button
                                              onClick={handleCancelEdit}
                                              className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-400 transition-colors flex items-center gap-1"
                                            >
                                              <X className="h-4 w-4" />
                                              Cancel
                                            </button>
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => handleStartEdit(row)}
                                            className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                            title="Edit Risk Assessment"
                                          >
                                            <Edit3 className="h-5 w-5" />
                                          </button>
                                        )}
                                      </div>
                                      <div className="space-y-5">
                                        <div className="pb-3 border-b border-gray-100">
                                          <label className="text-sm font-medium text-gray-600 block mb-2">Process Step</label>
                                          <p className="text-sm text-gray-900 font-medium">{row.stepName}</p>
                                        </div>
                                        <div className="pb-3 border-b border-gray-100">
                                          <label className="text-sm font-medium text-gray-600 block mb-2">Risk ID</label>
                                          <p className="text-sm font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded">{row.riskId}</p>
                                        </div>
                                        <div className="pb-3 border-b border-gray-100">
                                          <label className="text-sm font-medium text-gray-600 block mb-2">L4 Risk</label>
                                          <p className="text-sm text-gray-900 font-medium">{row.l4Risk || '-'}</p>
                                        </div>
                                        <div className="pb-3 border-b border-gray-100">
                                          <label className="text-sm font-medium text-gray-600 block mb-2">L4 Description</label>
                                          <p className="text-sm text-gray-700 leading-relaxed">{row.l4Description || '-'}</p>
                                        </div>
                                        <div className="pb-3 border-b border-gray-100">
                                          <div className="flex items-center justify-between mb-2">
                                            <label className="text-sm font-medium text-gray-600">Risk Statement</label>
                                            {row.rationaleValidationStatus && (
                                              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                                                row.rationaleValidationStatus === 'approved'
                                                  ? 'bg-green-100 text-green-800 border border-green-200'
                                                  : row.rationaleValidationStatus === 'rejected'
                                                  ? 'bg-red-100 text-red-800 border border-red-200'
                                                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                              }`}>
                                                {row.rationaleValidationStatus === 'approved' && <CheckCircle className="h-3 w-3" />}
                                                {row.rationaleValidationStatus === 'rejected' && <XCircle className="h-3 w-3" />}
                                                {row.rationaleValidationStatus === 'pending' && <Clock className="h-3 w-3" />}
                                                {row.rationaleValidationStatus === 'approved' ? 'Validated' : row.rationaleValidationStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
                                              </div>
                                            )}
                                          </div>
                                          <div className="bg-gray-50 p-3 rounded-lg border">
                                            {editingRowId === row.id ? (
                                              <textarea
                                                value={editedValues.rationale ?? row.rationale}
                                                onChange={(e) => handleFieldChange('rationale', e.target.value)}
                                                rows={4}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter risk statement..."
                                              />
                                            ) : (
                                              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                {formatRationaleText(row.rationale)}
                                              </p>
                                            )}
                                          </div>
                                          {editingRowId !== row.id && (
                                            <div className="mt-3 flex items-center gap-2">
                                              <button
                                                onClick={() => handleRationaleValidation(row.id, 'approved')}
                                                disabled={row.rationaleValidationStatus === 'approved'}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                  row.rationaleValidationStatus === 'approved'
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                                                }`}
                                              >
                                                <CheckCircle className="h-3.5 w-3.5" />
                                                Approve
                                              </button>
                                              <button
                                                onClick={() => handleRationaleValidation(row.id, 'rejected')}
                                                disabled={row.rationaleValidationStatus === 'rejected'}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                  row.rationaleValidationStatus === 'rejected'
                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                    : 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
                                                }`}
                                              >
                                                <XCircle className="h-3.5 w-3.5" />
                                                Reject
                                              </button>
                                              {row.rationaleValidatedBy && row.rationaleValidatedAt && (
                                                <span className="ml-auto text-xs text-gray-500">
                                                  by {row.rationaleValidatedBy} on {new Date(row.rationaleValidatedAt).toLocaleDateString()}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        <div>
                                          <label className="text-sm font-medium text-gray-600 block mb-3">Applied Risk Description</label>
                                          <div className="mt-2">
                                            <AppliedRiskDescriptionDisplay
                                              riskCause={row.riskCause}
                                              riskEvent={row.riskEvent}
                                              riskImpact={row.riskImpact}
                                              appliedRiskDescription={row.appliedRiskDescription}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Right column - Likelihood and Regulatory */}
                                    <div className="space-y-6">
                                      {/* Enhanced Likelihood Display - Smaller */}
                                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
                                        <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                                          <div className="p-1.5 bg-green-100 rounded-lg">
                                            <BarChart3 className="h-4 w-4 text-green-600" />
                                          </div>
                                          <h4 className="text-base font-semibold text-gray-900">Likelihood Assessment</h4>
                                        </div>
                                        <div className="mt-3">
                                          {editingRowId === row.id ? (
                                            <div className="space-y-3">
                                              {/* Likelihood and Impact scores are read-only - use Risk Scoring Modal to edit */}
                                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                <div className="flex items-center gap-2 mb-2">
                                                  <Info className="w-4 h-4 text-blue-600" />
                                                  <span className="text-sm font-medium text-blue-900">Risk Scores</span>
                                                </div>
                                                <p className="text-xs text-blue-800 mb-2">
                                                  Likelihood and Impact scores are calculated via structured assessment.
                                                  Use the "Edit Scoring" button to modify them.
                                                </p>
                                                <div className="grid grid-cols-2 gap-3 mt-2">
                                                  <div>
                                                    <label className="text-xs font-medium text-gray-600 block mb-1">Likelihood</label>
                                                    <div className="px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700">
                                                      {row.likelihood}/5
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <label className="text-xs font-medium text-gray-600 block mb-1">Impact</label>
                                                    <div className="px-3 py-2 bg-white border border-gray-300 rounded text-sm text-gray-700">
                                                      {row.impact}/5
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ) : (
                                            <EnhancedLikelihoodDisplay
                                              likelihood={row.likelihood}
                                              likelihoodRationale={row.likelihoodRationale}
                                              onEditScoring={() => handleOpenRiskScoring(row)}
                                              isManuallyAssessed={!!row.likelihoodScoreCalculated}
                                            />
                                          )}
                                        </div>
                                      </div>

                                      {/* Regulatory Analysis - Below Likelihood */}
                                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
                                        <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
                                          <div className="p-1.5 bg-purple-100 rounded-lg">
                                            <Shield className="h-4 w-4 text-purple-600" />
                                          </div>
                                          <h4 className="text-base font-semibold text-gray-900">Regulatory Analysis</h4>
                                        </div>
                                        <div className="space-y-4">
                                          <div className="pb-3 border-b border-gray-100">
                                            <label className="text-sm font-medium text-gray-600 block mb-2">Regulatory Taxonomy</label>
                                            {editingRowId === row.id ? (
                                              <input
                                                type="text"
                                                value={editedValues.regulatoryTaxonomy ?? row.regulatoryTaxonomy}
                                                onChange={(e) => handleFieldChange('regulatoryTaxonomy', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            ) : (
                                              <p className="text-sm text-gray-900 font-medium">{row.regulatoryTaxonomy || <span className="text-gray-400 italic text-xs">No frameworks identified for this risk</span>}</p>
                                            )}
                                          </div>
                                          <div className="pb-3 border-b border-gray-100">
                                            <label className="text-sm font-medium text-gray-600 block mb-2">Auditor Might Ask</label>
                                            {editingRowId === row.id ? (
                                              <textarea
                                                value={editedValues.auditorMightAsk ?? row.auditorMightAsk}
                                                onChange={(e) => handleFieldChange('auditorMightAsk', e.target.value)}
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            ) : (
                                              <p className="text-sm text-gray-700 leading-relaxed">{row.auditorMightAsk || '-'}</p>
                                            )}
                                          </div>
                                          <div>
                                            <label className="text-sm font-medium text-gray-600 block mb-2">Key Evidence Required</label>
                                            {editingRowId === row.id ? (
                                              <textarea
                                                value={editedValues.keyEvidenceRequired ?? row.keyEvidenceRequired}
                                                onChange={(e) => handleFieldChange('keyEvidenceRequired', e.target.value)}
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                              />
                                            ) : (
                                              <p className="text-sm text-gray-700 leading-relaxed">{row.keyEvidenceRequired || '-'}</p>
                                            )}
                                          </div>

                                          {/* Framework Violations & Citations */}
                                          <div className="pt-4 border-t border-gray-200">
                                            <div className="flex items-center justify-between mb-3">
                                              <div className="flex items-center gap-2">
                                                <AlertTriangle className="h-4 w-4 text-red-600" />
                                                <label className="text-sm font-semibold text-gray-900">Framework Violations & Compliance Gaps</label>
                                              </div>
                                              <button
                                                onClick={() => {
                                                  window.location.href = '/app/admin?tab=regulatory-frameworks';
                                                }}
                                                className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 flex items-center gap-1"
                                                title="View all regulatory frameworks"
                                              >
                                                <Shield className="h-3.5 w-3.5" />
                                                View All Frameworks
                                              </button>
                                            </div>
                                            <p className="text-xs text-gray-600 mb-3 italic">
                                              💡 Click any framework name below to view its complete details, standards, and requirements
                                            </p>
                                            {row.frameworkViolations && row.frameworkViolations.length > 0 ? (
                                              <div className="space-y-3">
                                                {/* Display detailed framework violations from AI */}
                                                {row.frameworkViolations.map((violation, idx) => {
                                                  const severityColors = {
                                                    critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-900', badge: 'bg-red-600 text-white' },
                                                    high: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900', badge: 'bg-orange-500 text-white' },
                                                    medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', badge: 'bg-yellow-500 text-white' },
                                                    low: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', badge: 'bg-blue-500 text-white' }
                                                  };
                                                  const colors = severityColors[violation.severity as keyof typeof severityColors] || severityColors.medium;

                                                  return (
                                                    <div key={idx} className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
                                                      <div className="flex items-start gap-3">
                                                        <div className="flex-shrink-0 w-6 h-6 bg-white border border-gray-400 rounded-full flex items-center justify-center">
                                                          <span className="text-xs font-bold text-gray-700">{idx + 1}</span>
                                                        </div>
                                                        <div className="flex-1 space-y-2">
                                                          {/* Header with Framework and Severity */}
                                                          <div className="flex items-center gap-2 flex-wrap">
                                                            <button
                                                              onClick={() => {
                                                                window.location.href = `/app/admin?tab=regulatory-frameworks&framework=${encodeURIComponent(violation.frameworkName)}`;
                                                              }}
                                                              className="px-2 py-1 bg-white border border-gray-400 text-gray-900 text-xs font-bold rounded shadow-sm hover:bg-blue-50 hover:border-blue-500 hover:text-blue-700 transition-colors cursor-pointer flex items-center gap-1"
                                                              title="Click to view framework details"
                                                            >
                                                              <ExternalLink className="h-3 w-3" />
                                                              {violation.frameworkName}
                                                            </button>
                                                            <span className={`px-2 py-1 ${colors.badge} text-xs font-bold rounded shadow-sm uppercase`}>
                                                              {violation.severity}
                                                            </span>
                                                          </div>

                                                          {/* Standard ID and Name */}
                                                          <div className={`${colors.text} bg-white bg-opacity-60 rounded px-2 py-1`}>
                                                            <p className="text-sm font-bold">
                                                              {violation.standardId}: {violation.standardName}
                                                            </p>
                                                          </div>

                                                          {/* Violation Description */}
                                                          <div>
                                                            <p className="text-xs font-semibold text-gray-900 mb-1 flex items-center gap-1">
                                                              <AlertTriangle className="h-3 w-3" />
                                                              Violation:
                                                            </p>
                                                            <p className="text-xs text-gray-800 leading-relaxed pl-4">
                                                              {violation.violationDescription}
                                                            </p>
                                                          </div>

                                                          {/* Requirement */}
                                                          {violation.requirement && (
                                                            <div className="bg-white bg-opacity-60 rounded px-3 py-2">
                                                              <p className="text-xs font-semibold text-gray-900 mb-1">Standard Requirement:</p>
                                                              <p className="text-xs text-gray-700 italic leading-relaxed">
                                                                "{violation.requirement}"
                                                              </p>
                                                            </div>
                                                          )}

                                                          {/* Citation References */}
                                                          {(violation.pageNumber || violation.sectionReference) && (
                                                            <div className="flex items-center gap-3 text-xs pt-2 border-t border-gray-300">
                                                              {violation.pageNumber && (
                                                                <div className="flex items-center gap-1 text-gray-700 font-medium">
                                                                  <FileText className="h-3 w-3" />
                                                                  <span>Page {violation.pageNumber}</span>
                                                                </div>
                                                              )}
                                                              {violation.sectionReference && (
                                                                <div className="flex items-center gap-1 text-gray-700 font-medium">
                                                                  <Shield className="h-3 w-3" />
                                                                  <span>{violation.sectionReference}</span>
                                                                </div>
                                                              )}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            ) : row.regulatoryTaxonomy && row.regulatoryTaxonomy.split(',').length > 0 ? (
                                              <div className="space-y-2">
                                                {/* Fallback to simple display */}
                                                {row.regulatoryTaxonomy.split(',').map((framework, idx) => (
                                                  <div key={idx} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                    <div className="flex items-start gap-2">
                                                      <div className="flex-shrink-0 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                                                        <span className="text-xs font-bold text-red-700">{idx + 1}</span>
                                                      </div>
                                                      <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                          <button
                                                            onClick={() => {
                                                              window.location.href = `/app/admin?tab=regulatory-frameworks&framework=${encodeURIComponent(framework.trim())}`;
                                                            }}
                                                            className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded hover:bg-red-200 hover:text-red-800 transition-colors cursor-pointer flex items-center gap-1"
                                                            title="Click to view framework details"
                                                          >
                                                            <ExternalLink className="h-3 w-3" />
                                                            {framework.trim()}
                                                          </button>
                                                          <span className="text-xs text-red-600 font-medium">Potential Violation</span>
                                                        </div>
                                                        <p className="text-xs text-gray-700 mb-2">
                                                          This process step may not comply with {framework.trim()} requirements.
                                                          Review framework standards and create detailed citations in Risk Mapping.
                                                        </p>
                                                        <div className="flex items-center gap-3 text-xs">
                                                          <div className="flex items-center gap-1 text-gray-600">
                                                            <FileText className="h-3 w-3" />
                                                            <span>Citation needed</span>
                                                          </div>
                                                          <div className="flex items-center gap-1 text-gray-600">
                                                            <Shield className="h-3 w-3" />
                                                            <span>Evidence required</span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                                                <Shield className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                                <p className="text-xs text-gray-600">No regulatory frameworks identified</p>
                                                <p className="text-xs text-gray-500 mt-1">AI will identify applicable frameworks during assessment</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Control Categories */}
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                                      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                                        <div className="p-2 bg-orange-100 rounded-lg">
                                          <Settings className="h-5 w-5 text-orange-600" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900">Control Categories</h4>
                                        {row.existing_controls && row.existing_controls.length > 0 && (
                                          <div className="group relative">
                                            <HelpCircle className="h-5 w-5 text-blue-600 hover:text-blue-700 cursor-help transition-colors" />
                                            <div className="invisible group-hover:visible absolute z-50 left-0 top-6 w-[500px] bg-white border border-gray-300 rounded-lg shadow-xl p-5 text-xs max-h-[600px] overflow-y-auto">
                                              <div className="font-bold text-gray-900 mb-3 text-sm">
                                                How Controls Are Classified
                                              </div>

                                              {/* Core Principle */}
                                              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4 rounded">
                                                <div className="font-semibold text-blue-900 mb-1">Core Principle:</div>
                                                <div className="text-blue-800">
                                                  Classification depends on <span className="font-semibold italic">when</span> the control acts relative to the risk event.
                                                </div>
                                              </div>

                                              {/* Preventive */}
                                              <div className="mb-4 pb-4 border-b border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                  <span className="text-lg">✋</span>
                                                  <span className="font-bold text-green-700">Preventive</span>
                                                </div>
                                                <div className="text-gray-700 mb-2">
                                                  Controls that <span className="font-semibold">stop</span> or <span className="font-semibold">block</span> the risk from happening in the first place.
                                                </div>
                                                <div className="text-gray-600">
                                                  <div className="font-semibold mb-1">Examples:</div>
                                                  <ul className="list-disc list-inside space-y-1 ml-2">
                                                    <li>Access controls and authentication</li>
                                                    <li>Approval required <span className="italic">before</span> action</li>
                                                    <li>Real-time validation that blocks transactions</li>
                                                    <li>Segregation of duties</li>
                                                  </ul>
                                                </div>
                                              </div>

                                              {/* Detective */}
                                              <div className="mb-4 pb-4 border-b border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                  <span className="text-lg">🔍</span>
                                                  <span className="font-bold text-blue-700">Detective</span>
                                                </div>
                                                <div className="text-gray-700 mb-2">
                                                  Controls that <span className="font-semibold">find</span> or <span className="font-semibold">identify</span> problems after they've already occurred.
                                                </div>
                                                <div className="text-gray-600">
                                                  <div className="font-semibold mb-1">Examples:</div>
                                                  <ul className="list-disc list-inside space-y-1 ml-2">
                                                    <li>Periodic reviews and audits</li>
                                                    <li>Monitoring reports and alerts</li>
                                                    <li>Reconciliations and exception reports</li>
                                                    <li>Post-transaction approvals</li>
                                                  </ul>
                                                </div>
                                              </div>

                                              {/* Corrective */}
                                              <div className="mb-4 pb-4 border-b border-gray-200">
                                                <div className="flex items-center gap-2 mb-2">
                                                  <span className="text-lg">🔧</span>
                                                  <span className="font-bold text-orange-700">Corrective</span>
                                                </div>
                                                <div className="text-gray-700 mb-2">
                                                  Controls that <span className="font-semibold">fix</span> problems after they've been detected.
                                                </div>
                                                <div className="text-gray-600">
                                                  <div className="font-semibold mb-1">Examples:</div>
                                                  <ul className="list-disc list-inside space-y-1 ml-2">
                                                    <li>Incident response procedures</li>
                                                    <li>Remediation and recovery plans</li>
                                                    <li>Root cause analysis</li>
                                                    <li>Corrective action processes</li>
                                                  </ul>
                                                </div>
                                              </div>

                                              {/* Note */}
                                              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
                                                <div className="font-semibold text-yellow-900 mb-1">Note:</div>
                                                <div className="text-yellow-800">
                                                  Some activities like "monitoring" can be either preventive (real-time blocking) or detective (periodic review).
                                                  The system analyzes timing and context to determine the correct classification.
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>

                                      {/* Display controls with classification reasoning */}
                                      {row.existing_controls && row.existing_controls.length > 0 ? (
                                        <div className="space-y-3">
                                          {row.existing_controls.map((control: any, index: number) => (
                                            <div key={index} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                                              <div className="flex items-start justify-between gap-2 mb-2">
                                                <div className="flex items-center gap-2">
                                                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                                    control.type === 'Preventive' ? 'bg-green-100 text-green-800' :
                                                    control.type === 'Detective' ? 'bg-blue-100 text-blue-800' :
                                                    control.type === 'Corrective' ? 'bg-orange-100 text-orange-800' :
                                                    'bg-gray-100 text-gray-800'
                                                  }`}>
                                                    {control.type || 'Unknown'}
                                                  </span>
                                                  {control.controlId && (
                                                    <span className="text-xs text-gray-500 font-mono">{control.controlId}</span>
                                                  )}
                                                </div>
                                                {control.classificationReasoning && (
                                                  <div className="group relative">
                                                    <Info className="h-4 w-4 text-blue-500 hover:text-blue-600 cursor-help transition-colors flex-shrink-0" />
                                                    <div className="invisible group-hover:visible absolute z-50 right-0 top-6 w-[420px] bg-white border-2 border-blue-200 rounded-lg shadow-2xl p-4 text-xs max-h-[500px] overflow-y-auto">
                                                      <div className="font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                                                        <span className="text-blue-600">🔍</span>
                                                        <span>Classification Reasoning</span>
                                                      </div>

                                                      {/* Explanatory Legend */}
                                                      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mb-4 rounded">
                                                        <div className="font-semibold text-blue-900 mb-2 text-xs">💡 How It Works:</div>
                                                        <div className="text-blue-800 space-y-1 text-xs">
                                                          <div>• Keywords in control description add confidence points</div>
                                                          <div>• Category with highest score (≥0.40) wins</div>
                                                          <div>• <span className="inline-block w-3 h-3 bg-green-100 rounded mr-1"></span>Green = Preventive keywords</div>
                                                          <div>• <span className="inline-block w-3 h-3 bg-blue-100 rounded mr-1"></span>Blue = Detective keywords</div>
                                                          <div>• <span className="inline-block w-3 h-3 bg-orange-100 rounded mr-1"></span>Orange = Corrective keywords</div>
                                                        </div>
                                                      </div>

                                                      {/* Parse and Group Keywords by Category */}
                                                      {(() => {
                                                        const reasons = control.classificationReasoning.split(';').map((r: string) => r.trim()).filter(Boolean);
                                                        const categories: any = { preventive: [], detective: [], corrective: [], other: [] };
                                                        let hasWarning = false;

                                                        reasons.forEach((reason: string) => {
                                                          if (reason.includes('⚠️') || reason.toLowerCase().includes('low confidence')) {
                                                            hasWarning = true;
                                                            return;
                                                          }

                                                          const match = reason.match(/(Preventive|Detective|Corrective):\s*(.+)/i);
                                                          if (match) {
                                                            const category = match[1].toLowerCase();
                                                            categories[category].push(match[2]);
                                                          } else {
                                                            categories.other.push(reason);
                                                          }
                                                        });

                                                        return (
                                                          <div className="space-y-3">
                                                            {hasWarning && (
                                                              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded mb-3">
                                                                <div className="font-semibold text-yellow-800">⚠️ Low Confidence</div>
                                                                <div className="text-yellow-700 text-xs mt-1">Manual review recommended</div>
                                                              </div>
                                                            )}

                                                            <div className="font-semibold text-gray-700 mb-2">Keywords Detected:</div>

                                                            {/* Preventive */}
                                                            {categories.preventive.length > 0 && (
                                                              <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                                                                <div className="font-semibold text-green-800 mb-2 flex items-center gap-1">
                                                                  🟢 Preventive Category
                                                                </div>
                                                                <div className="space-y-1">
                                                                  {categories.preventive.map((detail: string, i: number) => (
                                                                    <div key={i} className="text-green-900 text-xs flex items-start gap-1.5">
                                                                      <span className="mt-0.5">•</span>
                                                                      <span>{detail}</span>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              </div>
                                                            )}

                                                            {/* Detective */}
                                                            {categories.detective.length > 0 && (
                                                              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                                                                <div className="font-semibold text-blue-800 mb-2 flex items-center gap-1">
                                                                  🔵 Detective Category
                                                                </div>
                                                                <div className="space-y-1">
                                                                  {categories.detective.map((detail: string, i: number) => (
                                                                    <div key={i} className="text-blue-900 text-xs flex items-start gap-1.5">
                                                                      <span className="mt-0.5">•</span>
                                                                      <span>{detail}</span>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              </div>
                                                            )}

                                                            {/* Corrective */}
                                                            {categories.corrective.length > 0 && (
                                                              <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded">
                                                                <div className="font-semibold text-orange-800 mb-2 flex items-center gap-1">
                                                                  🟠 Corrective Category
                                                                </div>
                                                                <div className="space-y-1">
                                                                  {categories.corrective.map((detail: string, i: number) => (
                                                                    <div key={i} className="text-orange-900 text-xs flex items-start gap-1.5">
                                                                      <span className="mt-0.5">•</span>
                                                                      <span>{detail}</span>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              </div>
                                                            )}

                                                            {/* Other reasoning */}
                                                            {categories.other.length > 0 && (
                                                              <div className="bg-gray-50 border-l-4 border-gray-400 p-3 rounded">
                                                                <div className="space-y-1">
                                                                  {categories.other.map((detail: string, i: number) => (
                                                                    <div key={i} className="text-gray-700 text-xs flex items-start gap-1.5">
                                                                      <span className="mt-0.5">•</span>
                                                                      <span>{detail}</span>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                              </div>
                                                            )}

                                                            {/* Result */}
                                                            <div className="pt-3 border-t border-gray-200 mt-3">
                                                              <div className="flex items-center gap-2 text-gray-700">
                                                                <span className="font-semibold">✅ Result:</span>
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                                  control.type === 'Preventive' ? 'bg-green-100 text-green-800' :
                                                                  control.type === 'Detective' ? 'bg-blue-100 text-blue-800' :
                                                                  control.type === 'Corrective' ? 'bg-orange-100 text-orange-800' :
                                                                  'bg-gray-100 text-gray-800'
                                                                }`}>
                                                                  {control.type}
                                                                </span>
                                                              </div>
                                                            </div>
                                                          </div>
                                                        );
                                                      })()}

                                                      <div className="absolute -top-2 right-4 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-blue-200"></div>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                              <p className="text-sm text-gray-700 leading-relaxed">{control.description}</p>

                                              {/* Citation/Source */}
                                              {control.source && (
                                                <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-600 bg-gray-50 p-2 rounded border-l-2 border-blue-400">
                                                  <FileText className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                  <span className="italic">{control.source}</span>
                                                </div>
                                              )}

                                              {control.frequency && (
                                                <div className="flex flex-wrap gap-3 mt-2 pt-2 border-t border-gray-100 text-xs text-gray-600">
                                                  <span>Frequency: {control.frequency}</span>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="flex flex-wrap gap-2">
                                          {row.typicalControlCategories.split(', ').map((control: string, index: number) => (
                                            <span key={index} className="px-3 py-2 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
                                              {control}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    {/* Business Impact */}
                                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
                                      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                                        <div className="p-2 bg-red-100 rounded-lg">
                                          <AlertTriangle className="h-5 w-5 text-red-600" />
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-900">Business Impact</h4>
                                      </div>
                                      {editingRowId === row.id ? (
                                        <textarea
                                          value={editedValues.businessImpactTranslation ?? row.businessImpactTranslation}
                                          onChange={(e) => handleFieldChange('businessImpactTranslation', e.target.value)}
                                          rows={4}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                      ) : (
                                        <p className="text-sm text-gray-700 leading-relaxed">{row.businessImpactTranslation || '-'}</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Assessment Metadata */}
                                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                                    <div className="text-xs text-gray-500">
                                      Last assessed: {row.lastAssessed} by {row.assessedBy}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        row.reviewStatus === 'approved' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-yellow-100 text-yellow-800'
                                      }`}>
                                        {row.reviewStatus === 'approved' ? 'Approved' : 'Pending Review'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {sortedGroupedData.length === 0 && !loading && (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-blue-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Ready for Risk Assessment</h3>
            <div className="space-y-4">
              {processMapSteps.length === 0 ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl mx-auto">
                  <div className="text-red-800">
                    <p className="font-semibold mb-2">❌ No Process Steps Found</p>
                    {error ? (
                      <p className="text-sm mb-3">{error}</p>
                    ) : (
                      <p className="text-sm mb-3">
                        No process steps are available for risk assessment. This could be due to:
                      </p>
                    )}
                    <ul className="text-sm text-left space-y-1 mb-3">
                      <li>• Project doesn't have any process maps created</li>
                      <li>• Process maps are empty or not properly configured</li>
                      <li>• API connectivity issues preventing data loading</li>
                    </ul>
                    <p className="text-sm">
                      <strong>Resolution:</strong> Please go to the Process Maps section and create/verify your process flowcharts first.
                    </p>
                  </div>
                </div>
              ) : (
                <div className={`${partialDataCleared ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'} rounded-lg p-4 max-w-2xl mx-auto`}>
                  <div className={partialDataCleared ? 'text-orange-800' : 'text-green-800'}>
                    {partialDataCleared ? (
                      <>
                        <p className="font-semibold mb-2">🧹 Interrupted Generation Cleared - Ready for Fresh Generation</p>
                        <p className="text-sm mb-3">
                          Detected very early-stage interrupted risk assessment data (likely from browser refresh during the first few steps) and cleared it automatically.
                        </p>
                        <div className="bg-white rounded p-3 mb-3 text-xs">
                          <p className="text-orange-700 font-medium">
                            ⚠️ Browser was refreshed very early during risk generation - minimal incomplete data was removed to ensure complete assessment.
                          </p>
                        </div>
                        <p className="text-sm">
                          Click <strong>"Generate Risk Assessment"</strong> to create fresh, complete assessments for all {processMapSteps.length} process steps.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="font-semibold mb-2">✅ Process Steps Successfully Loaded!</p>
                        <p className="text-sm mb-3">
                          Found <strong>{processMapSteps.length} process steps</strong> from your flowchart:
                        </p>
                        <div className="bg-white rounded p-3 mb-3 text-xs">
                          <ul className="text-left space-y-1">
                            {processMapSteps.slice(0, 5).map((step, index) => (
                              <li key={index} className="truncate">
                                • {step.name}
                              </li>
                            ))}
                            {processMapSteps.length > 5 && (
                              <li className="text-gray-500">... and {processMapSteps.length - 5} more steps</li>
                            )}
                          </ul>
                        </div>
                        <p className="text-sm">
                          Click <strong>"Generate Risk Assessment"</strong> button above to create comprehensive risk assessments with regulatory analysis.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Total Assessments</div>
          <div className="text-2xl font-bold text-gray-900">{riskAssessmentData.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">High/Critical Risks</div>
          <div className="text-2xl font-bold text-red-600">
            {riskAssessmentData.filter((r: RiskAssessmentRow) => ['HIGH', 'CRITICAL'].includes(r.inherentRiskLevel)).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Avg Priority Score</div>
          <div className="text-2xl font-bold text-orange-600">
            {riskAssessmentData.length > 0 
              ? Math.round(riskAssessmentData.reduce((sum: number, r: RiskAssessmentRow) => sum + r.riskPriorityScore, 0) / riskAssessmentData.length) 
              : 0}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-sm text-gray-600">Pending Reviews</div>
          <div className="text-2xl font-bold text-yellow-600">
            {riskAssessmentData.filter((r: RiskAssessmentRow) => r.reviewStatus === 'pending').length}
          </div>
        </div>
      </div>

      {/* Audit Trail Modal */}
      {selectedRiskForAudit && (
        <AuditTrailModal
          riskAssessmentId={selectedRiskForAudit.id}
          riskId={selectedRiskForAudit.riskId}
          isOpen={auditTrailModalOpen}
          onClose={handleCloseAuditTrail}
        />
      )}

      {/* Risk Scoring Modal */}
      {selectedRiskForScoring && projectId && (
        <RiskScoringModal
          isOpen={scoringModalOpen}
          onClose={handleCloseRiskScoring}
          riskId={selectedRiskForScoring.riskId}
          projectId={projectId}
          processStepName={selectedRiskForScoring.processStepName}
          currentLikelihood={selectedRiskForScoring.likelihood}
          currentImpact={selectedRiskForScoring.impact}
          controlEffectiveness={selectedRiskForScoring.controlEffectiveness}
          onSaveComplete={handleScoringComplete}
        />
      )}

      {/* Risk Scoping Wizard Modal */}
      {showRiskScopingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-auto m-4">
            <div className="sticky top-0 bg-[#221551] text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Risk Scoping Configuration</h2>
              <button
                onClick={() => setShowRiskScopingModal(false)}
                className="text-white hover:text-gray-300 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <RiskScopingWizard
                tenantId={effectiveTenantId}
                onComplete={() => {
                  setShowRiskScopingModal(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Modal for Risk Assessment */}
      {showQuestionnaireModal && projectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <div className="sticky top-0 bg-[#221551] text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold">Risk Scoping Questionnaire</h2>
              <button
                onClick={() => setShowQuestionnaireModal(false)}
                className="text-white hover:text-gray-300 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <ProcessScopingQuestionnaire
                projectId={projectId}
                tenantId={effectiveTenantId}
                onComplete={(result: any) => {
                  console.log('🎯🎯🎯 PARENT: onComplete callback triggered!');
                  console.log('🎯 PARENT: Received result:', result);
                  console.log('🎯 PARENT: applicableRiskIds:', result?.applicableRiskIds);

                  setScopingResult(result);
                  console.log('🎯 PARENT: scopingResult state updated');

                  setShowQuestionnaireModal(false);
                  console.log('🎯 PARENT: Modal closed');

                  // Now generate assessments with filtered risks
                  // ✅ Pass result directly to avoid React state update delay
                  console.log('🎯 PARENT: Calling performComprehensiveAssessmentBackground with result...');
                  performComprehensiveAssessmentBackground(result);
                }}
                onSkip={() => {
                  console.log('⏭️ Scoping skipped - assessing all risks');
                  setScopingResult(null);
                  setShowQuestionnaireModal(false);

                  // Generate without filtering
                  performComprehensiveAssessmentBackground();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Confirmation Dialog */}
      {cancelConfirmDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            {/* Header with Icon */}
            <div className={`p-6 ${
              cancelConfirmDialog.type === 'cancel' ? 'bg-red-50' :
              cancelConfirmDialog.type === 'warning' ? 'bg-yellow-50' :
              'bg-green-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  cancelConfirmDialog.type === 'cancel' ? 'bg-red-100' :
                  cancelConfirmDialog.type === 'warning' ? 'bg-yellow-100' :
                  'bg-green-100'
                }`}>
                  {cancelConfirmDialog.type === 'cancel' && (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  {cancelConfirmDialog.type === 'warning' && (
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                  )}
                  {cancelConfirmDialog.type === 'success' && (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  )}
                </div>
                <h3 className={`text-lg font-semibold ${
                  cancelConfirmDialog.type === 'cancel' ? 'text-red-900' :
                  cancelConfirmDialog.type === 'warning' ? 'text-yellow-900' :
                  'text-green-900'
                }`}>
                  {cancelConfirmDialog.title}
                </h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                {cancelConfirmDialog.message}
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
              {cancelConfirmDialog.type === 'cancel' ? (
                <>
                  <button
                    onClick={() => setCancelConfirmDialog({ ...cancelConfirmDialog, open: false })}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                  >
                    Keep Running
                  </button>
                  <button
                    onClick={cancelConfirmDialog.onConfirm}
                    className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Yes, Cancel Job
                  </button>
                </>
              ) : (
                <button
                  onClick={cancelConfirmDialog.onConfirm}
                  className={`px-4 py-2 text-white rounded-lg font-medium transition-colors ${
                    cancelConfirmDialog.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentTable;